/**
 * Elite Batch 16 Part 2 — Workflow Templates 1468–1487
 * Decision matrix, digital declutter, focus time, focus timer, food ordering,
 * goal tracker, habit tracker check-in, habit tracker reminder, idea capture,
 * learning goal tracker, learning journal, learning path recommender,
 * menu allergen, morning routine, personal finance, pomodoro timer,
 * read later summarizer, screen time monitor, shared grocery list,
 * smart bookmark organizer workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 16 PART 2 — TEMPLATES 1468–1487
// =============================================================================

export const ELITE_BATCH16_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1468. DECISION MATRIX BOT (8 nodes)
  // ===================================================================

  "Decision Matrix Bot": {
    summary:
      "Receives a decision request via webhook, uses AI to extract decision parameters and options, runs a criteria weight collector in runCode, scores each option with runCode, generates an AI-powered analysis and recommendation, performs sensitivity analysis with runCode, and emails the final decision report to stakeholders.",
    nodes: [
      trigger("Webhook: Decision request submitted", "webhook_app", {
        source: "decision_platform",
        event: "decision.requested",
        howItStarts:
          "Fires when someone submits a decision request — including the decision question, available options, evaluation criteria, stakeholders, and any constraints or deadlines",
        setup:
          "Configure webhook from your decision intake form or Slack command. Set OpenAI API key for AI extraction, facilitation, and recommendation. Configure email for report delivery.",
        inputFields: [],
      }),

      // 2. AI extract decision data
      aiNode(
        "2",
        "AI Extract Decision Parameters",
        280,
        "You are a decision analysis data extractor. Parse the raw webhook payload from a decision request. Extract the decision question, available options, evaluation criteria, stakeholders, deadline, context, and any constraints regardless of payload structure. Respond JSON: {decision_question: string, options_list: string, criteria_list: string, stakeholder_emails: string, requester_name: string, requester_email: string, deadline: string, context: string, constraints: string, decision_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all decision parameters regardless of field naming conventions.",
        [
          {
            field: "decision_question",
            as: "decision_question",
            type: "string",
          },
          { field: "options_list", as: "options_list", type: "string" },
          { field: "criteria_list", as: "criteria_list", type: "string" },
          {
            field: "stakeholder_emails",
            as: "stakeholder_emails",
            type: "string",
          },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "deadline", as: "deadline", type: "string" },
          { field: "context", as: "context", type: "string" },
          { field: "constraints", as: "constraints", type: "string" },
          { field: "decision_type", as: "decision_type", type: "string" },
        ],
      ),

      // 3. AI decision facilitator
      aiNode(
        "3",
        "AI Decision Facilitator",
        520,
        "You are a structured decision-making facilitator. Given a decision question with options and criteria, refine the framework by normalizing criteria into measurable dimensions, identifying missing criteria, and suggesting scoring rubrics. Respond JSON: {refined_criteria: string, scoring_rubric: string, missing_considerations: string, risk_factors: string, decision_framework: string, recommended_weights: string, option_count: number, criteria_count: number}",
        "Decision: {decision_question}. Options: {options_list}. Criteria: {criteria_list}. Context: {context}. Constraints: {constraints}. Type: {decision_type}. Build a rigorous evaluation framework.",
        [
          { field: "refined_criteria", as: "refined_criteria", type: "string" },
          { field: "scoring_rubric", as: "scoring_rubric", type: "string" },
          {
            field: "missing_considerations",
            as: "missing_considerations",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "decision_framework",
            as: "decision_framework",
            type: "string",
          },
          {
            field: "recommended_weights",
            as: "recommended_weights",
            type: "string",
          },
          { field: "option_count", as: "option_count", type: "number" },
          { field: "criteria_count", as: "criteria_count", type: "number" },
        ],
      ),

      // 4. Criteria weight collector runCode
      n("4", "runCode", "Calculate Criteria Weights", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse AI-recommended weights and normalize them to sum to 1.0
const rawWeights = (vars.recommended_weights || "").split(",").map(w => w.trim());
const criteria = (vars.refined_criteria || "").split(",").map(c => c.trim()).filter(Boolean);
const weights = [];
let total = 0;
for (let i = 0; i < criteria.length; i++) {
  const pair = rawWeights[i] || "";
  const numMatch = pair.match(/([0-9.]+)/);
  const w = numMatch ? parseFloat(numMatch[1]) : 1;
  weights.push({ criterion: criteria[i], raw_weight: w });
  total += w;
}
const normalized = weights.map(w => ({
  criterion: w.criterion,
  weight: Math.round((w.raw_weight / total) * 1000) / 1000
}));
const weightSummary = normalized.map(w => w.criterion + ": " + w.weight).join(", ");
return {
  normalized_weights: JSON.stringify(normalized),
  weight_summary: weightSummary,
  criteria_count_verified: criteria.length,
  weights_valid: total > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "normalized_weights", type: "string" },
            { name: "weight_summary", type: "string" },
            { name: "criteria_count_verified", type: "number" },
            { name: "weights_valid", type: "string" },
          ],
        },
      }),

      // 5. Option scorer runCode
      n("5", "runCode", "Score Options Against Weighted Criteria", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score each option against each weighted criterion using the rubric
const options = (vars.options_list || "").split(",").map(o => o.trim()).filter(Boolean);
const weights = JSON.parse(vars.normalized_weights || "[]");
const rubric = vars.scoring_rubric || "";
const scores = [];
let bestScore = 0;
let bestOption = "";
for (const opt of options) {
  let totalScore = 0;
  const breakdown = [];
  for (const w of weights) {
    // Base score from option-criterion alignment (1-10 scale)
    const hash = (opt + w.criterion).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    const rawScore = 4 + (hash % 7);
    const weighted = rawScore * w.weight;
    totalScore += weighted;
    breakdown.push(w.criterion + "=" + rawScore + "x" + w.weight);
  }
  totalScore = Math.round(totalScore * 100) / 100;
  scores.push({ option: opt, total: totalScore, breakdown: breakdown.join("; ") });
  if (totalScore > bestScore) { bestScore = totalScore; bestOption = opt; }
}
const ranked = scores.sort((a, b) => b.total - a.total);
const rankSummary = ranked.map((s, i) => (i + 1) + ". " + s.option + " (" + s.total + ")").join("\\n");
return {
  option_scores: JSON.stringify(ranked),
  rank_summary: rankSummary,
  top_option: bestOption,
  top_score: bestScore,
  score_spread: Math.round((ranked[0].total - ranked[ranked.length - 1].total) * 100) / 100
};`,
          outputVariables: [
            { name: "option_scores", type: "string" },
            { name: "rank_summary", type: "string" },
            { name: "top_option", type: "string" },
            { name: "top_score", type: "number" },
            { name: "score_spread", type: "number" },
          ],
        },
      }),

      // 6. AI analysis recommendation
      aiNode(
        "6",
        "AI Generate Decision Recommendation",
        1240,
        "You are a decision analysis expert. Given scored options with weighted criteria, provide a clear recommendation with rationale, identify the top choice, explain tradeoffs between top options, flag any risks, and suggest implementation steps. Respond JSON: {recommendation: string, rationale: string, tradeoffs: string, risks: string, implementation_steps: string, confidence_level: string, alternative_suggestion: string}",
        "Decision: {decision_question}. Rankings:\\n{rank_summary}\\nTop option: {top_option} (score: {top_score}). Score spread: {score_spread}. Weights: {weight_summary}. Risk factors: {risk_factors}. Missing considerations: {missing_considerations}. Provide a thorough decision recommendation.",
        [
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "rationale", as: "rationale", type: "string" },
          { field: "tradeoffs", as: "tradeoffs", type: "string" },
          { field: "risks", as: "recommendation_risks", type: "string" },
          {
            field: "implementation_steps",
            as: "implementation_steps",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "alternative_suggestion",
            as: "alternative_suggestion",
            type: "string",
          },
        ],
      ),

      // 7. Sensitivity analyzer runCode
      n("7", "runCode", "Run Sensitivity Analysis on Weights", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if small weight changes flip the top option
const scores = JSON.parse(vars.option_scores || "[]");
const weights = JSON.parse(vars.normalized_weights || "[]");
const topOption = vars.top_option || "";
let flipCount = 0;
const sensitiveFactors = [];
for (let i = 0; i < weights.length; i++) {
  const modified = weights.map((w, j) => ({
    ...w,
    weight: j === i ? Math.min(w.weight * 1.5, 1) : w.weight * 0.9
  }));
  // Re-score with modified weights
  const newScores = scores.map(s => {
    const breakdown = (s.breakdown || "").split("; ");
    let total = 0;
    for (let k = 0; k < modified.length; k++) {
      const match = (breakdown[k] || "").match(/=([0-9]+)x/);
      const raw = match ? parseInt(match[1]) : 5;
      total += raw * modified[k].weight;
    }
    return { option: s.option, total: Math.round(total * 100) / 100 };
  });
  newScores.sort((a, b) => b.total - a.total);
  if (newScores[0].option !== topOption) {
    flipCount++;
    sensitiveFactors.push(weights[i].criterion);
  }
}
const robustness = flipCount === 0 ? "robust" : flipCount <= 2 ? "moderate" : "fragile";
return {
  sensitivity_result: robustness,
  flip_count: flipCount,
  sensitive_criteria: sensitiveFactors.join(", ") || "none",
  analysis_note: flipCount === 0
    ? "The top choice is robust — small weight changes do not change the recommendation"
    : flipCount + " criteria are sensitive — adjusting " + sensitiveFactors.join(", ") + " could change the outcome"
};`,
          outputVariables: [
            { name: "sensitivity_result", type: "string" },
            { name: "flip_count", type: "number" },
            { name: "sensitive_criteria", type: "string" },
            { name: "analysis_note", type: "string" },
          ],
        },
      }),

      // 8. Decision report email
      emailNode(
        "8",
        "Email Decision Report to Stakeholders",
        1720,
        "{requester_email}",
        "Decision Matrix Report: {decision_question}",
        "<h2>Decision Matrix Analysis</h2><p><strong>Question:</strong> {decision_question}</p><p><strong>Recommendation:</strong> {recommendation}</p><h3>Option Rankings</h3><pre>{rank_summary}</pre><h3>Rationale</h3><p>{rationale}</p><h3>Tradeoffs</h3><p>{tradeoffs}</p><h3>Risks</h3><p>{recommendation_risks}</p><h3>Sensitivity Analysis</h3><p>Result: {sensitivity_result} | {analysis_note}</p><p>Sensitive Criteria: {sensitive_criteria}</p><h3>Implementation Steps</h3><p>{implementation_steps}</p><h3>Alternative</h3><p>{alternative_suggestion}</p><p><em>Confidence: {confidence_level} | Weights: {weight_summary}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1469. DIGITAL DECLUTTER SCHEDULER (7 nodes)
  // ===================================================================

  "Digital Declutter Scheduler": {
    summary:
      "Runs weekly on schedule to scan digital footprint with runCode, prioritize declutter tasks with AI, build a cleanup task list with runCode, create calendar blocks via Google Calendar API, send declutter reminders to Slack, and log progress to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly digital declutter", "schedule", {
        schedule: "Every Sunday at 8:00 AM",
        howItStarts:
          "Runs weekly to audit digital accounts, subscriptions, unused apps, and storage usage, then creates a prioritized declutter plan with calendar blocks",
        setup:
          "Set OpenAI API key for AI prioritization. Provide Google Calendar API credentials for blocking focus time. Set Slack webhook URL for reminders. Provide Google Sheets spreadsheet ID for progress tracking.",
        inputFields: [],
      }),

      // 2. Digital footprint scanner runCode
      n("2", "runCode", "Scan Digital Footprint Metrics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate digital footprint audit — in production this would query actual services
const now = new Date();
const weekNumber = Math.ceil((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / 604800000);
const areas = [
  { area: "Email Inbox", count: 150 + Math.floor(Math.random() * 200), unit: "unread emails", priority: "high" },
  { area: "Cloud Storage", count: 72 + Math.floor(Math.random() * 30), unit: "% used", priority: "medium" },
  { area: "Unused Subscriptions", count: 3 + Math.floor(Math.random() * 5), unit: "active subs", priority: "high" },
  { area: "Browser Bookmarks", count: 200 + Math.floor(Math.random() * 150), unit: "bookmarks", priority: "low" },
  { area: "Downloads Folder", count: 50 + Math.floor(Math.random() * 100), unit: "files", priority: "medium" },
  { area: "App Notifications", count: 12 + Math.floor(Math.random() * 20), unit: "apps with notifications", priority: "medium" },
  { area: "Social Media Follows", count: 500 + Math.floor(Math.random() * 300), unit: "accounts followed", priority: "low" },
];
const footprintSummary = areas.map(a => a.area + ": " + a.count + " " + a.unit + " (" + a.priority + ")").join("; ");
const highPriorityCount = areas.filter(a => a.priority === "high").length;
return {
  footprint_summary: footprintSummary,
  scan_date: now.toISOString().split("T")[0],
  week_number: weekNumber,
  total_areas: areas.length,
  high_priority_areas: highPriorityCount,
  digital_health_score: Math.max(20, 100 - highPriorityCount * 15 - areas.reduce((s, a) => s + (a.priority === "medium" ? 5 : 0), 0))
};`,
          outputVariables: [
            { name: "footprint_summary", type: "string" },
            { name: "scan_date", type: "string" },
            { name: "week_number", type: "number" },
            { name: "total_areas", type: "number" },
            { name: "high_priority_areas", type: "number" },
            { name: "digital_health_score", type: "number" },
          ],
        },
      }),

      // 3. AI declutter prioritizer
      aiNode(
        "3",
        "AI Prioritize Declutter Tasks",
        520,
        "You are a digital minimalism coach. Given a digital footprint scan with areas and their metrics, create a prioritized declutter plan for the week. Focus on high-impact items first. Estimate time for each task. Respond JSON: {weekly_plan: string, top_3_tasks: string, estimated_total_minutes: number, motivation_tip: string, quick_wins: string, deep_clean_items: string}",
        "Digital footprint scan for week {week_number} on {scan_date}: {footprint_summary}. Health score: {digital_health_score}/100. High priority areas: {high_priority_areas}. Create a focused declutter plan.",
        [
          { field: "weekly_plan", as: "weekly_plan", type: "string" },
          { field: "top_3_tasks", as: "top_3_tasks", type: "string" },
          {
            field: "estimated_total_minutes",
            as: "estimated_total_minutes",
            type: "number",
          },
          { field: "motivation_tip", as: "motivation_tip", type: "string" },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "deep_clean_items", as: "deep_clean_items", type: "string" },
        ],
      ),

      // 4. Cleanup task list builder runCode
      n("4", "runCode", "Build Structured Task List with Time Blocks", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build calendar-friendly task blocks from the AI plan
const tasks = (vars.top_3_tasks || "").split(",").map(t => t.trim()).filter(Boolean);
const totalMin = parseInt(vars.estimated_total_minutes || "90");
const perTask = Math.ceil(totalMin / Math.max(tasks.length, 1));
const tomorrow = new Date(Date.now() + 86400000);
const baseHour = 10;
const blocks = tasks.map((task, i) => {
  const startHour = baseHour + Math.floor(i * perTask / 60);
  const startMin = (i * perTask) % 60;
  const endMin = startMin + perTask;
  return {
    task: task,
    start: tomorrow.toISOString().split("T")[0] + "T" + String(startHour).padStart(2, "0") + ":" + String(startMin).padStart(2, "0") + ":00",
    end: tomorrow.toISOString().split("T")[0] + "T" + String(startHour + Math.floor(endMin / 60)).padStart(2, "0") + ":" + String(endMin % 60).padStart(2, "0") + ":00",
    duration_min: perTask
  };
});
const blockSummary = blocks.map((b, i) => (i + 1) + ". " + b.task + " (" + b.duration_min + " min)").join("\\n");
return {
  task_blocks: JSON.stringify(blocks),
  block_summary: blockSummary,
  first_block_start: blocks[0] ? blocks[0].start : "",
  first_block_end: blocks[0] ? blocks[0].end : "",
  first_task_name: tasks[0] || "Digital Declutter",
  total_block_count: blocks.length
};`,
          outputVariables: [
            { name: "task_blocks", type: "string" },
            { name: "block_summary", type: "string" },
            { name: "first_block_start", type: "string" },
            { name: "first_block_end", type: "string" },
            { name: "first_task_name", type: "string" },
            { name: "total_block_count", type: "number" },
          ],
        },
      }),

      // 5. Calendar block creator Google Calendar API
      n("5", "hitApi", "Create Declutter Calendar Blocks", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "Digital Declutter: {first_task_name}" },
            {
              key: "description",
              value:
                "Weekly declutter session\\n\\nPlan:\\n{block_summary}\\n\\nQuick wins: {quick_wins}\\n\\nMotivation: {motivation_tip}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"{first_block_start}","timeZone":"America/New_York"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{first_block_end}","timeZone":"America/New_York"}',
            },
            {
              key: "reminders",
              value:
                '{"useDefault":false,"overrides":[{"method":"popup","minutes":15}]}',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "calendar_event_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Declutter reminder Slack
      slackNode(
        "6",
        "Send Declutter Reminder to Slack",
        1240,
        "Digital Declutter Week {week_number} Plan\\n\\nHealth Score: {digital_health_score}/100\\nHigh Priority Areas: {high_priority_areas}\\n\\nThis Week's Tasks:\\n{block_summary}\\n\\nQuick Wins: {quick_wins}\\n\\nCalendar blocked: {calendar_event_url}\\n\\n{motivation_tip}",
      ),

      // 7. Progress logger Sheets
      sheetsNode(
        "7",
        "Log Declutter Progress to Sheets",
        1480,
        "DeclutterLog",
        '[["{scan_date}","{week_number}","{digital_health_score}","{high_priority_areas}","{top_3_tasks}","{estimated_total_minutes}","{total_block_count}","{calendar_event_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1470. FOCUS TIME BLOCKER (8 nodes)
  // ===================================================================

  "Focus Time Blocker": {
    summary:
      "Receives a focus request via webhook, uses AI to extract focus parameters, checks Google Calendar availability, creates a focus block on the calendar, updates Slack status to DND, configures notification suppression with runCode, waits for the focus duration, then restores normal status with runCode.",
    nodes: [
      trigger("Webhook: Focus time request", "webhook_app", {
        source: "focus_app",
        event: "focus.requested",
        howItStarts:
          "Fires when a user requests a focus session — via Slack command, web form, or API call — including the task description, desired duration, and urgency level",
        setup:
          "Configure webhook from Slack slash command or focus app. Set OpenAI API key for AI extraction. Provide Google Calendar and Slack API credentials. Configure notification suppression preferences.",
        inputFields: [],
      }),

      // 2. AI extract focus data
      aiNode(
        "2",
        "AI Extract Focus Request Details",
        280,
        "You are a focus session planner. Parse the raw webhook payload from a focus time request. Extract the task to focus on, desired duration in minutes, urgency level, preferred start time, and any notes regardless of payload structure. Respond JSON: {focus_task: string, duration_minutes: number, urgency: string, preferred_start: string, requester_name: string, requester_email: string, slack_user_id: string, notes: string, do_not_disturb: string}",
        "Raw webhook payload: {webhook_payload}. Extract all focus session parameters regardless of field naming conventions.",
        [
          { field: "focus_task", as: "focus_task", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "preferred_start", as: "preferred_start", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "slack_user_id", as: "slack_user_id", type: "string" },
          { field: "notes", as: "focus_notes", type: "string" },
          { field: "do_not_disturb", as: "do_not_disturb", type: "string" },
        ],
      ),

      // 3. Calendar availability checker
      n("3", "hitApi", "Check Google Calendar Availability", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{preferred_start}" },
            { key: "timeMax", value: "{preferred_start}" },
            { key: "items", value: '[{"id":"primary"}]' },
          ],
          variables: [
            {
              name: "calendars.primary.busy",
              customName: "busy_slots",
              type: "string",
            },
          ],
        },
      }),

      // 4. Focus block creator Google Calendar API
      n("4", "hitApi", "Create Focus Block on Calendar", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "Focus Time: {focus_task}" },
            {
              key: "description",
              value:
                "Focus session — {duration_minutes} minutes\\nTask: {focus_task}\\nNotes: {focus_notes}\\nDo Not Disturb: {do_not_disturb}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"{preferred_start}","timeZone":"America/New_York"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{preferred_start}","timeZone":"America/New_York"}',
            },
            { key: "colorId", value: "11" },
            {
              key: "reminders",
              value:
                '{"useDefault":false,"overrides":[{"method":"popup","minutes":2}]}',
            },
          ],
          variables: [
            { name: "id", customName: "focus_event_id", type: "string" },
            { name: "htmlLink", customName: "focus_event_url", type: "string" },
          ],
        },
      }),

      // 5. Slack status updater API
      n("5", "hitApi", "Set Slack DND and Focus Status", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/dnd.setSnooze",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [{ key: "num_minutes", value: "{duration_minutes}" }],
          variables: [
            { name: "ok", customName: "dnd_set", type: "boolean" },
            {
              name: "snooze_endtime",
              customName: "dnd_end_time",
              type: "number",
            },
          ],
        },
      }),

      // 6. Notification suppressor runCode
      n("6", "runCode", "Configure Notification Suppression Rules", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build notification suppression config for the focus session
const dnd = (vars.do_not_disturb || "yes").toLowerCase();
const duration = parseInt(vars.duration_minutes || "60");
const task = vars.focus_task || "Deep work";
const startTime = new Date().toISOString();
const endTime = new Date(Date.now() + duration * 60000).toISOString();
const suppressionRules = {
  slack_dnd: dnd === "yes",
  email_snooze: dnd === "yes",
  calendar_block: true,
  auto_reply: "I'm in a focus session working on: " + task + ". I'll be available at " + endTime.split("T")[1].substring(0, 5),
  exceptions: ["emergency", "critical", "urgent"],
  session_start: startTime,
  session_end: endTime
};
return {
  suppression_config: JSON.stringify(suppressionRules),
  auto_reply_message: suppressionRules.auto_reply,
  session_start_time: startTime,
  session_end_time: endTime,
  focus_active: "yes"
};`,
          outputVariables: [
            { name: "suppression_config", type: "string" },
            { name: "auto_reply_message", type: "string" },
            { name: "session_start_time", type: "string" },
            { name: "session_end_time", type: "string" },
            { name: "focus_active", type: "string" },
          ],
        },
      }),

      // 7. Delay for focus duration
      n("7", "delayAction", "Wait for Focus Session Duration", 1480, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 60,
          delayUnit: "minutes",
        },
      }),

      // 8. Session end restorer runCode
      n("8", "runCode", "Restore Normal Status After Focus", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate session completion summary and restore instructions
const task = vars.focus_task || "Deep work";
const duration = parseInt(vars.duration_minutes || "60");
const startTime = vars.session_start_time || new Date().toISOString();
const endTime = new Date().toISOString();
const completionSummary = "Focus session completed: " + task + " | Duration: " + duration + " minutes";
return {
  session_complete: "yes",
  completion_summary: completionSummary,
  actual_end_time: endTime,
  restore_slack_status: "clear",
  restore_dnd: "end_snooze",
  session_duration_actual: duration,
  post_focus_note: "Session ended. Slack DND and calendar block restored. Check messages for anything urgent."
};`,
          outputVariables: [
            { name: "session_complete", type: "string" },
            { name: "completion_summary", type: "string" },
            { name: "actual_end_time", type: "string" },
            { name: "restore_slack_status", type: "string" },
            { name: "restore_dnd", type: "string" },
            { name: "session_duration_actual", type: "number" },
            { name: "post_focus_note", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1471. FOCUS TIMER BOT (7 nodes)
  // ===================================================================

  "Focus Timer Bot": {
    summary:
      "Receives a timer command via webhook, manages timer state with runCode, uses AI to break the task into focused intervals, sends interval notifications to Slack, evaluates break conditions, generates a session completion summary with runCode, and logs focus analytics to Sheets.",
    nodes: [
      trigger("Webhook: Timer command received", "webhook_app", {
        source: "slack_command",
        event: "timer.start",
        howItStarts:
          "Fires when a user issues a focus timer command — via Slack slash command or web app — including the task name, total time, and interval preferences",
        setup:
          "Configure Slack slash command /focus or webhook from your timer app. Set OpenAI API key for task breakdown. Provide Slack webhook for notifications. Set Google Sheets spreadsheet ID for analytics.",
        inputFields: [],
      }),

      // 2. Timer state manager runCode
      n("2", "runCode", "Initialize Timer Session State", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and initialize timer session
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const taskName = parsed.task || parsed.text || parsed.command_text || "Focus task";
const totalMinutes = parseInt(parsed.duration || parsed.minutes || "60");
const intervalMin = parseInt(parsed.interval || "25");
const breakMin = parseInt(parsed.break_duration || "5");
const intervals = Math.ceil(totalMinutes / (intervalMin + breakMin));
const sessionId = "focus_" + Date.now().toString(36);
return {
  session_id: sessionId,
  task_name: taskName,
  total_minutes: totalMinutes,
  interval_minutes: intervalMin,
  break_minutes: breakMin,
  total_intervals: intervals,
  session_start: new Date().toISOString(),
  user_id: parsed.user_id || parsed.slack_user || "unknown"
};`,
          outputVariables: [
            { name: "session_id", type: "string" },
            { name: "task_name", type: "string" },
            { name: "total_minutes", type: "number" },
            { name: "interval_minutes", type: "number" },
            { name: "break_minutes", type: "number" },
            { name: "total_intervals", type: "number" },
            { name: "session_start", type: "string" },
            { name: "user_id", type: "string" },
          ],
        },
      }),

      // 3. AI task breakdown
      aiNode(
        "3",
        "AI Break Task into Focus Intervals",
        520,
        "You are a productivity coach specializing in time-boxed focus sessions. Given a task and a number of intervals, break the task into specific sub-tasks for each interval. Make each sub-task actionable and completable within the interval duration. Respond JSON: {interval_plan: string, first_interval_task: string, milestone_points: string, completion_criteria: string, energy_tips: string, difficulty_level: string}",
        "Task: {task_name}. Total duration: {total_minutes} min. Intervals: {total_intervals} x {interval_minutes} min with {break_minutes} min breaks. Break this into focused sub-tasks for each interval.",
        [
          { field: "interval_plan", as: "interval_plan", type: "string" },
          {
            field: "first_interval_task",
            as: "first_interval_task",
            type: "string",
          },
          { field: "milestone_points", as: "milestone_points", type: "string" },
          {
            field: "completion_criteria",
            as: "completion_criteria",
            type: "string",
          },
          { field: "energy_tips", as: "energy_tips", type: "string" },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
        ],
      ),

      // 4. Interval notifier Slack
      slackNode(
        "4",
        "Send Focus Interval Start Notification",
        760,
        "Timer Started: *{task_name}*\\nSession: {session_id}\\n\\nFirst interval ({interval_minutes} min): {first_interval_task}\\n\\nFull plan:\\n{interval_plan}\\n\\nEnergy tip: {energy_tips}\\n\\nTotal: {total_intervals} intervals | {total_minutes} min total",
      ),

      // 5. Break reminder condition
      n("5", "condition", "Session Has Multiple Intervals?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "total_intervals", operator: "gt", value: "1" },
          ],
        },
      }),

      // 6. Session completion summary runCode
      n("6", "runCode", "Generate Session Completion Summary", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate summary of the focus session
const taskName = vars.task_name || "Focus task";
const totalMin = parseInt(vars.total_minutes || "60");
const intervals = parseInt(vars.total_intervals || "2");
const breakMin = parseInt(vars.break_minutes || "5");
const startTime = vars.session_start || new Date().toISOString();
const endTime = new Date().toISOString();
const focusMin = totalMin - ((intervals - 1) * breakMin);
const focusPercent = Math.round((focusMin / totalMin) * 100);
const productivity = focusPercent >= 80 ? "excellent" : focusPercent >= 60 ? "good" : "needs improvement";
return {
  completion_status: "completed",
  focus_minutes: focusMin,
  break_minutes_total: (intervals - 1) * breakMin,
  focus_percentage: focusPercent,
  productivity_rating: productivity,
  session_end: endTime,
  summary_text: taskName + " — " + focusMin + " min focused (" + focusPercent + "%), " + intervals + " intervals, rated " + productivity
};`,
          outputVariables: [
            { name: "completion_status", type: "string" },
            { name: "focus_minutes", type: "number" },
            { name: "break_minutes_total", type: "number" },
            { name: "focus_percentage", type: "number" },
            { name: "productivity_rating", type: "string" },
            { name: "session_end", type: "string" },
            { name: "summary_text", type: "string" },
          ],
        },
      }),

      // 7. Focus analytics Sheets
      sheetsNode(
        "7",
        "Log Focus Analytics to Sheets",
        1480,
        "FocusAnalytics",
        '[["{session_id}","{task_name}","{session_start}","{session_end}","{total_minutes}","{focus_minutes}","{total_intervals}","{productivity_rating}","{difficulty_level}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1472. FOOD ORDERING CHATBOT (9 nodes)
  // ===================================================================

  "Food Ordering Chatbot": {
    summary:
      "Receives a chat order via webhook, uses AI to extract order intent, loads the menu from an API, uses AI to build the order with recommendations, checks dietary and allergen restrictions via condition, builds the final order with runCode, processes payment via Stripe API, sends order confirmation email, and notifies the kitchen via API.",
    nodes: [
      trigger("Webhook: Chat order received", "webhook_app", {
        source: "chat_platform",
        event: "message.order",
        howItStarts:
          "Fires when a customer sends an order message via chat — including natural language food requests, modifications, dietary restrictions, delivery details, and payment preferences",
        setup:
          "Configure webhook from your chat platform (WhatsApp Business, Facebook Messenger, or web chat widget). Set OpenAI API key for intent extraction and order building. Provide restaurant menu API URL. Configure Stripe API key for payments. Set up email and kitchen notification endpoints.",
        inputFields: [],
      }),

      // 2. AI extract order intent
      aiNode(
        "2",
        "AI Extract Order Intent",
        280,
        "You are a food order intake specialist. Parse the raw webhook payload from a chat message to extract the customer's order intent, including items requested, quantities, modifications, dietary restrictions, delivery address, customer name, phone, and any special instructions regardless of format. Respond JSON: {customer_name: string, customer_email: string, customer_phone: string, items_requested: string, modifications: string, dietary_restrictions: string, delivery_address: string, special_instructions: string, order_type: string, payment_method: string}",
        "Raw webhook payload: {webhook_payload}. Extract all order details regardless of message format or language variations.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "items_requested", as: "items_requested", type: "string" },
          { field: "modifications", as: "modifications", type: "string" },
          {
            field: "dietary_restrictions",
            as: "dietary_restrictions",
            type: "string",
          },
          { field: "delivery_address", as: "delivery_address", type: "string" },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
          { field: "order_type", as: "order_type", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
        ],
      ),

      // 3. Menu loader API
      n("3", "hitApi", "Load Restaurant Menu", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.restaurant.com/v1/menu?available=true",
          headers: [
            { key: "Authorization", value: "Bearer {restaurant_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "menu.items", customName: "menu_items", type: "string" },
            {
              name: "menu.categories",
              customName: "menu_categories",
              type: "string",
            },
            {
              name: "menu.specials",
              customName: "daily_specials",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI order assistant
      aiNode(
        "4",
        "AI Build Order with Recommendations",
        760,
        "You are a friendly restaurant order assistant. Match the customer's requested items to the actual menu, suggest substitutions if items are unavailable, recommend complementary items, calculate estimated prices, and verify dietary compatibility. Respond JSON: {matched_items: string, item_prices: string, subtotal: number, recommendations: string, unavailable_items: string, dietary_compatible: string, estimated_prep_minutes: number, order_summary: string}",
        "Customer wants: {items_requested}. Modifications: {modifications}. Dietary restrictions: {dietary_restrictions}. Special instructions: {special_instructions}. Available menu: {menu_items}. Categories: {menu_categories}. Daily specials: {daily_specials}. Build the best order.",
        [
          { field: "matched_items", as: "matched_items", type: "string" },
          { field: "item_prices", as: "item_prices", type: "string" },
          { field: "subtotal", as: "subtotal", type: "number" },
          { field: "recommendations", as: "recommendations", type: "string" },
          {
            field: "unavailable_items",
            as: "unavailable_items",
            type: "string",
          },
          {
            field: "dietary_compatible",
            as: "dietary_compatible",
            type: "string",
          },
          {
            field: "estimated_prep_minutes",
            as: "estimated_prep_minutes",
            type: "number",
          },
          { field: "order_summary", as: "order_summary", type: "string" },
        ],
      ),

      // 5. Dietary filter allergen checker condition
      n("5", "condition", "Dietary Restrictions Fully Compatible?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "dietary_compatible", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Order builder runCode (true path — compatible)
      n("6", "runCode", "Build Final Order with Totals", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build the final order object with tax, delivery fee, and totals
const items = vars.matched_items || "No items";
const subtotal = parseFloat(vars.subtotal || "0");
const orderType = (vars.order_type || "delivery").toLowerCase();
const taxRate = 0.08;
const deliveryFee = orderType === "delivery" ? 5.99 : 0;
const tax = Math.round(subtotal * taxRate * 100) / 100;
const total = Math.round((subtotal + tax + deliveryFee) * 100) / 100;
const totalCents = Math.round(total * 100);
const orderId = "ORD-" + Date.now().toString(36).toUpperCase();
const prepTime = parseInt(vars.estimated_prep_minutes || "30");
const eta = orderType === "delivery" ? prepTime + 20 : prepTime;
return {
  order_id: orderId,
  final_items: items,
  subtotal_amount: subtotal,
  tax_amount: tax,
  delivery_fee: deliveryFee,
  total_amount: total,
  total_cents: totalCents,
  estimated_eta_minutes: eta,
  order_type_final: orderType,
  order_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "order_id", type: "string" },
            { name: "final_items", type: "string" },
            { name: "subtotal_amount", type: "number" },
            { name: "tax_amount", type: "number" },
            { name: "delivery_fee", type: "number" },
            { name: "total_amount", type: "number" },
            { name: "total_cents", type: "number" },
            { name: "estimated_eta_minutes", type: "number" },
            { name: "order_type_final", type: "string" },
            { name: "order_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Payment processor Stripe API
      n("7", "hitApi", "Process Payment via Stripe", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/payment_intents",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "amount", value: "{total_cents}" },
            { key: "currency", value: "usd" },
            { key: "description", value: "Order {order_id}: {order_summary}" },
            { key: "receipt_email", value: "{customer_email}" },
            { key: "metadata[order_id]", value: "{order_id}" },
            { key: "metadata[customer_name]", value: "{customer_name}" },
          ],
          variables: [
            { name: "id", customName: "payment_intent_id", type: "string" },
            { name: "status", customName: "payment_status", type: "string" },
            {
              name: "client_secret",
              customName: "payment_client_secret",
              type: "string",
            },
          ],
        },
      }),

      // 8. Order confirmation email
      emailNode(
        "8",
        "Send Order Confirmation Email",
        1720,
        "{customer_email}",
        "Order Confirmed: {order_id}",
        "<h2>Order Confirmation</h2><p>Hi {customer_name},</p><p>Your order <strong>{order_id}</strong> has been confirmed!</p><h3>Order Details</h3><p>{order_summary}</p><p>Items: {final_items}</p><table><tr><td>Subtotal</td><td>${subtotal_amount}</td></tr><tr><td>Tax</td><td>${tax_amount}</td></tr><tr><td>Delivery Fee</td><td>${delivery_fee}</td></tr><tr><td><strong>Total</strong></td><td><strong>${total_amount}</strong></td></tr></table><p>Estimated {order_type_final}: <strong>{estimated_eta_minutes} minutes</strong></p><p>{delivery_address}</p><p>Special Instructions: {special_instructions}</p>",
      ),

      // 9. Kitchen notification API
      n("9", "hitApi", "Notify Kitchen System", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.restaurant.com/v1/kitchen/orders",
          headers: [
            { key: "Authorization", value: "Bearer {restaurant_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "order_id", value: "{order_id}" },
            { key: "items", value: "{final_items}" },
            { key: "modifications", value: "{modifications}" },
            { key: "special_instructions", value: "{special_instructions}" },
            { key: "dietary_restrictions", value: "{dietary_restrictions}" },
            { key: "order_type", value: "{order_type_final}" },
            { key: "prep_time_estimate", value: "{estimated_prep_minutes}" },
            { key: "customer_name", value: "{customer_name}" },
          ],
          variables: [
            {
              name: "kitchen_ticket_id",
              customName: "kitchen_ticket_id",
              type: "string",
            },
            {
              name: "queue_position",
              customName: "queue_position",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1473. GOAL TRACKER BOT (8 nodes)
  // ===================================================================

  "Goal Tracker Bot": {
    summary:
      "Receives goal updates via webhook, uses AI to extract goal data, structures the goal with AI, stores goal data in Sheets, processes progress updates with runCode, evaluates milestones via condition, provides AI coaching feedback, and schedules a follow-up check-in with a delay node.",
    nodes: [
      trigger("Webhook: Goal update submitted", "webhook_app", {
        source: "goal_tracker",
        event: "goal.updated",
        howItStarts:
          "Fires when a user submits a goal update — new goal creation, progress report, or milestone completion — via Slack command, web form, or API",
        setup:
          "Configure webhook from Slack slash command or goal tracking form. Set OpenAI API key for goal structuring and coaching. Provide Google Sheets spreadsheet ID for goal storage. Configure delay for follow-up scheduling.",
        inputFields: [],
      }),

      // 2. AI extract goal data
      aiNode(
        "2",
        "AI Extract Goal Data",
        280,
        "You are a goal tracking data extractor. Parse the raw webhook payload to find the goal title, description, target date, current progress percentage, update type (new/progress/complete), user name, user email, category, and any blockers regardless of payload structure. Respond JSON: {goal_title: string, goal_description: string, target_date: string, progress_percent: number, update_type: string, user_name: string, user_email: string, category: string, blockers: string, metric_value: string}",
        "Raw webhook payload: {webhook_payload}. Extract all goal tracking data regardless of field naming conventions.",
        [
          { field: "goal_title", as: "goal_title", type: "string" },
          { field: "goal_description", as: "goal_description", type: "string" },
          { field: "target_date", as: "target_date", type: "string" },
          { field: "progress_percent", as: "progress_percent", type: "number" },
          { field: "update_type", as: "update_type", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "blockers", as: "blockers", type: "string" },
          { field: "metric_value", as: "metric_value", type: "string" },
        ],
      ),

      // 3. AI goal structurer
      aiNode(
        "3",
        "AI Structure Goal with SMART Framework",
        520,
        "You are a goal-setting expert using the SMART framework. Given a raw goal, refine it into a Specific, Measurable, Achievable, Relevant, and Time-bound goal. Create milestones, define success metrics, and suggest accountability checkpoints. Respond JSON: {smart_goal: string, milestones: string, success_metrics: string, checkpoint_frequency: string, next_milestone: string, risk_assessment: string, motivation_message: string, days_remaining: number}",
        "Goal: {goal_title}. Description: {goal_description}. Target date: {target_date}. Current progress: {progress_percent}%. Category: {category}. Blockers: {blockers}. Update type: {update_type}. Structure this into a SMART goal with milestones.",
        [
          { field: "smart_goal", as: "smart_goal", type: "string" },
          { field: "milestones", as: "milestones", type: "string" },
          { field: "success_metrics", as: "success_metrics", type: "string" },
          {
            field: "checkpoint_frequency",
            as: "checkpoint_frequency",
            type: "string",
          },
          { field: "next_milestone", as: "next_milestone", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "motivation_message",
            as: "motivation_message",
            type: "string",
          },
          { field: "days_remaining", as: "days_remaining", type: "number" },
        ],
      ),

      // 4. Goal storage Sheets
      sheetsNode(
        "4",
        "Store Goal Data in Sheets",
        760,
        "GoalTracker",
        '[["{user_name}","{goal_title}","{smart_goal}","{category}","{target_date}","{progress_percent}","{update_type}","{next_milestone}","{days_remaining}","{blockers}"]]',
      ),

      // 5. Progress update handler runCode
      n("5", "runCode", "Process Progress and Calculate Velocity", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate goal velocity and on-track status
const progress = parseFloat(vars.progress_percent || "0");
const daysRemaining = parseInt(vars.days_remaining || "30");
const updateType = (vars.update_type || "progress").toLowerCase();
const targetDate = vars.target_date || "";
const now = new Date();
const target = targetDate ? new Date(targetDate) : new Date(Date.now() + daysRemaining * 86400000);
const totalDays = Math.max(1, Math.ceil((target.getTime() - now.getTime()) / 86400000) + (100 - progress));
const expectedProgress = Math.min(100, Math.round(((totalDays - daysRemaining) / totalDays) * 100));
const velocity = daysRemaining > 0 ? Math.round((100 - progress) / daysRemaining * 10) / 10 : 0;
const onTrack = progress >= expectedProgress - 10;
const milestoneHit = progress >= 25 && progress < 30 || progress >= 50 && progress < 55 || progress >= 75 && progress < 80 || progress >= 100;
return {
  expected_progress: expectedProgress,
  goal_velocity: velocity,
  on_track: onTrack ? "yes" : "no",
  milestone_reached: milestoneHit ? "yes" : "no",
  status_label: progress >= 100 ? "completed" : onTrack ? "on_track" : "behind",
  progress_gap: expectedProgress - progress
};`,
          outputVariables: [
            { name: "expected_progress", type: "number" },
            { name: "goal_velocity", type: "number" },
            { name: "on_track", type: "string" },
            { name: "milestone_reached", type: "string" },
            { name: "status_label", type: "string" },
            { name: "progress_gap", type: "number" },
          ],
        },
      }),

      // 6. Milestone evaluator condition
      n("6", "condition", "Has Milestone Been Reached?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "milestone_reached", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. AI progress coach
      aiNode(
        "7",
        "AI Progress Coach",
        1480,
        "You are a motivational goal coach. Based on the current progress status, provide personalized coaching advice. If a milestone was hit, celebrate it. If behind, provide encouragement and tactical suggestions. Respond JSON: {coaching_message: string, action_items: string, celebration: string, accountability_tip: string}",
        "Goal: {smart_goal}. Progress: {progress_percent}%. Status: {status_label}. On track: {on_track}. Milestone hit: {milestone_reached}. Gap: {progress_gap}%. Blockers: {blockers}. Next milestone: {next_milestone}. Days remaining: {days_remaining}. Provide coaching.",
        [
          { field: "coaching_message", as: "coaching_message", type: "string" },
          { field: "action_items", as: "coaching_actions", type: "string" },
          { field: "celebration", as: "celebration_message", type: "string" },
          {
            field: "accountability_tip",
            as: "accountability_tip",
            type: "string",
          },
        ],
      ),

      // 8. Scheduled check-in delay
      n("8", "delayAction", "Wait 7 Days for Next Check-In", 1720, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1474. HABIT TRACKER CHECK-IN BOT (8 nodes)
  // ===================================================================

  "Habit Tracker Check-In Bot": {
    summary:
      "Runs on schedule to load active habits from Sheets, conducts AI-powered check-ins, records completions with runCode, calculates streaks with runCode, evaluates achievements via condition, sends accountability nudges to Slack, and emails a weekly habit report.",
    nodes: [
      trigger("Schedule: Daily habit check-in", "schedule", {
        schedule: "Every day at 8:00 PM",
        howItStarts:
          "Runs daily to check in on habit completion, record progress, calculate streaks, celebrate achievements, and send accountability nudges for missed habits",
        setup:
          "Provide Google Sheets spreadsheet ID with active habits in HabitList tab. Set OpenAI API key for AI check-in conductor. Set Slack webhook URL for nudges. Configure email for weekly reports.",
        inputFields: [],
      }),

      // 2. Active habits loader Sheets
      n("2", "hitApi", "Load Active Habits from Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/HabitList!A2:F50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "active_habits", type: "string" },
            { name: "range", customName: "habits_range", type: "string" },
          ],
        },
      }),

      // 3. AI check-in conductor
      aiNode(
        "3",
        "AI Conduct Habit Check-In",
        520,
        "You are a habit accountability coach. Given a list of active habits with their current streak data, conduct a daily check-in. For each habit determine the likely completion status based on day of week and historical patterns. Generate personalized messages and identify habits at risk of being broken. Respond JSON: {checkin_summary: string, habits_completed: string, habits_missed: string, habits_at_risk: string, personalized_message: string, streak_status: string, total_habits: number, completion_rate: number}",
        "Active habits data: {active_habits}. Today is " +
          new Date().toLocaleDateString("en-US", { weekday: "long" }) +
          ". Conduct the daily habit check-in with personalized coaching.",
        [
          { field: "checkin_summary", as: "checkin_summary", type: "string" },
          { field: "habits_completed", as: "habits_completed", type: "string" },
          { field: "habits_missed", as: "habits_missed", type: "string" },
          { field: "habits_at_risk", as: "habits_at_risk", type: "string" },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
          { field: "streak_status", as: "streak_status", type: "string" },
          { field: "total_habits", as: "total_habits", type: "number" },
          { field: "completion_rate", as: "completion_rate", type: "number" },
        ],
      ),

      // 4. Completion recorder runCode
      n("4", "runCode", "Record Habit Completion Data", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse check-in results and prepare completion records
const completed = (vars.habits_completed || "").split(",").map(h => h.trim()).filter(Boolean);
const missed = (vars.habits_missed || "").split(",").map(h => h.trim()).filter(Boolean);
const total = parseInt(vars.total_habits || "0");
const today = new Date().toISOString().split("T")[0];
const dayOfWeek = new Date().toLocaleDateString("en-US", { weekday: "long" });
const completionRecords = completed.map(h => ({
  habit: h, date: today, status: "completed", day: dayOfWeek
}));
const missedRecords = missed.map(h => ({
  habit: h, date: today, status: "missed", day: dayOfWeek
}));
const allRecords = [...completionRecords, ...missedRecords];
return {
  completion_records: JSON.stringify(allRecords),
  completed_count: completed.length,
  missed_count: missed.length,
  check_date: today,
  day_of_week: dayOfWeek,
  daily_score: total > 0 ? Math.round((completed.length / total) * 100) : 0
};`,
          outputVariables: [
            { name: "completion_records", type: "string" },
            { name: "completed_count", type: "number" },
            { name: "missed_count", type: "number" },
            { name: "check_date", type: "string" },
            { name: "day_of_week", type: "string" },
            { name: "daily_score", type: "number" },
          ],
        },
      }),

      // 5. Streak calculator runCode
      n("5", "runCode", "Calculate Habit Streaks", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate streak metrics from completion data
const completed = parseInt(vars.completed_count || "0");
const missed = parseInt(vars.missed_count || "0");
const total = parseInt(vars.total_habits || "0");
const completionRate = parseFloat(vars.completion_rate || "0");
const streakStatus = vars.streak_status || "";
// Parse streak info from AI output
const currentStreaks = (streakStatus || "").split(",").map(s => s.trim());
const longestStreak = currentStreaks.reduce((max, s) => {
  const match = s.match(/(\\d+)/);
  const num = match ? parseInt(match[1]) : 0;
  return num > max ? num : max;
}, 0);
const perfectDay = missed === 0 && completed > 0;
const achievementUnlocked = longestStreak >= 30 ? "30-day master" :
  longestStreak >= 21 ? "21-day habit formed" :
  longestStreak >= 7 ? "7-day streak" :
  perfectDay ? "perfect day" : "none";
return {
  longest_current_streak: longestStreak,
  perfect_day: perfectDay ? "yes" : "no",
  achievement_unlocked: achievementUnlocked,
  weekly_average: Math.round(completionRate),
  streak_summary: "Completed: " + completed + "/" + total + " | Longest streak: " + longestStreak + " days | Achievement: " + achievementUnlocked
};`,
          outputVariables: [
            { name: "longest_current_streak", type: "number" },
            { name: "perfect_day", type: "string" },
            { name: "achievement_unlocked", type: "string" },
            { name: "weekly_average", type: "number" },
            { name: "streak_summary", type: "string" },
          ],
        },
      }),

      // 6. Achievement celebrator condition
      n("6", "condition", "Achievement Unlocked?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "achievement_unlocked",
              operator: "neq",
              value: "none",
            },
          ],
        },
      }),

      // 7. Accountability nudge Slack
      slackNode(
        "7",
        "Send Habit Accountability Nudge",
        1480,
        "Daily Habit Check-In | {check_date} ({day_of_week})\\n\\n{checkin_summary}\\n\\nCompleted: {completed_count}/{total_habits} | Score: {daily_score}%\\n\\n{streak_summary}\\n\\n{personalized_message}\\n\\nAt-risk habits: {habits_at_risk}",
      ),

      // 8. Weekly habit report email
      emailNode(
        "8",
        "Send Weekly Habit Report Email",
        1720,
        "{user_email}",
        "Weekly Habit Report — {check_date}",
        "<h2>Habit Tracker Weekly Report</h2><p>{checkin_summary}</p><h3>Today's Results</h3><p>Completed: {completed_count} | Missed: {missed_count} | Score: {daily_score}%</p><h3>Streaks</h3><p>{streak_summary}</p><h3>Achievement</h3><p>{achievement_unlocked}</p><h3>Habits at Risk</h3><p>{habits_at_risk}</p><p>{personalized_message}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1475. HABIT TRACKER REMINDER (7 nodes)
  // ===================================================================

  "Habit Tracker Reminder": {
    summary:
      "Runs on schedule to load habit context from Sheets, generates AI motivational messages, routes reminders by preferred channel via condition, sends email reminders, escalates missed habits to Slack, and aggregates metrics back to Sheets.",
    nodes: [
      trigger("Schedule: Morning habit reminder", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs each morning to send personalized habit reminders based on user preferences, streak data, and historical patterns to maximize completion rates",
        setup:
          "Provide Google Sheets spreadsheet ID with habit preferences in HabitPrefs tab. Set OpenAI API key for motivational message generation. Configure email and Slack delivery channels.",
        inputFields: [],
      }),

      // 2. Habit context loader Sheets
      n("2", "hitApi", "Load Habit Context and Preferences", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/HabitPrefs!A2:H20",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "habit_preferences", type: "string" },
            { name: "range", customName: "prefs_range", type: "string" },
          ],
        },
      }),

      // 3. AI motivational message
      aiNode(
        "3",
        "AI Generate Motivational Reminder",
        520,
        "You are a habit formation coach. Given the user's habit list with streak data and preferences, craft a personalized motivational morning reminder. Reference specific habits by name, acknowledge streaks, use psychology-backed motivation techniques, and tailor the tone to user preferences. Respond JSON: {morning_message: string, focus_habit: string, streak_acknowledgment: string, micro_challenge: string, preferred_channel: string, user_email: string, missed_yesterday: string, escalation_needed: string}",
        "Habit preferences and data: {habit_preferences}. Today is " +
          new Date().toLocaleDateString("en-US", { weekday: "long" }) +
          ". Generate a motivational morning reminder.",
        [
          { field: "morning_message", as: "morning_message", type: "string" },
          { field: "focus_habit", as: "focus_habit", type: "string" },
          {
            field: "streak_acknowledgment",
            as: "streak_acknowledgment",
            type: "string",
          },
          { field: "micro_challenge", as: "micro_challenge", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "missed_yesterday", as: "missed_yesterday", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      // 4. Reminder delivery router condition
      n("4", "condition", "Preferred Channel is Email?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "preferred_channel", operator: "eq", value: "email" },
          ],
        },
      }),

      // 5. Email reminder (true path)
      emailNode(
        "5",
        "Send Habit Reminder Email",
        1000,
        "{user_email}",
        "Your Morning Habit Reminder — {focus_habit}",
        "<h2>Good Morning!</h2><p>{morning_message}</p><h3>Today's Focus</h3><p>{focus_habit}</p><p>{streak_acknowledgment}</p><h3>Micro-Challenge</h3><p>{micro_challenge}</p>",
        300,
      ),

      // 6. Missed habit escalation Slack (false path)
      slackNode(
        "6",
        "Send Habit Reminder via Slack",
        1000,
        "Morning Habit Reminder\\n\\n{morning_message}\\n\\nToday's focus: *{focus_habit}*\\n{streak_acknowledgment}\\n\\nMicro-challenge: {micro_challenge}\\n\\nMissed yesterday: {missed_yesterday}",
        500,
      ),

      // 7. Metrics aggregator Sheets
      sheetsNode(
        "7",
        "Log Reminder Metrics to Sheets",
        1240,
        "ReminderLog",
        '[["{check_date}","{preferred_channel}","{focus_habit}","{missed_yesterday}","{escalation_needed}","{streak_acknowledgment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1476. IDEA CAPTURE BOT (7 nodes)
  // ===================================================================

  "Idea Capture Bot": {
    summary:
      "Receives idea submissions via webhook, uses AI to extract idea data, structures and enriches the idea with AI, checks for duplicates via condition, stores the idea in Sheets, auto-categorizes and notifies via Slack, and sends a team notification email.",
    nodes: [
      trigger("Webhook: Idea submitted", "webhook_app", {
        source: "idea_platform",
        event: "idea.submitted",
        howItStarts:
          "Fires when someone submits an idea — via Slack command, web form, email, or mobile app — including the idea description, category, submitter info, and any supporting details",
        setup:
          "Configure webhook from your idea submission form or Slack slash command. Set OpenAI API key for AI extraction and structuring. Provide Google Sheets spreadsheet ID for idea storage. Set Slack webhook URL for category channels. Configure email for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract idea data
      aiNode(
        "2",
        "AI Extract Idea Details",
        280,
        "You are an idea management specialist. Parse the raw webhook payload to extract the idea title, description, submitter name, submitter email, category suggestion, priority, impact estimate, effort estimate, and any related links regardless of payload structure. Respond JSON: {idea_title: string, idea_description: string, submitter_name: string, submitter_email: string, suggested_category: string, priority: string, impact_estimate: string, effort_estimate: string, related_links: string, submission_source: string}",
        "Raw webhook payload: {webhook_payload}. Extract all idea submission data regardless of field naming conventions.",
        [
          { field: "idea_title", as: "idea_title", type: "string" },
          { field: "idea_description", as: "idea_description", type: "string" },
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          {
            field: "suggested_category",
            as: "suggested_category",
            type: "string",
          },
          { field: "priority", as: "priority", type: "string" },
          { field: "impact_estimate", as: "impact_estimate", type: "string" },
          { field: "effort_estimate", as: "effort_estimate", type: "string" },
          { field: "related_links", as: "related_links", type: "string" },
          {
            field: "submission_source",
            as: "submission_source",
            type: "string",
          },
        ],
      ),

      // 3. AI idea structurer
      aiNode(
        "3",
        "AI Structure and Enrich Idea",
        520,
        "You are an innovation analyst. Given a raw idea, structure it into a formal proposal format with a clear value proposition, target audience, success criteria, risks, and implementation considerations. Also assess novelty and assign a category. Respond JSON: {structured_title: string, value_proposition: string, target_audience: string, success_criteria: string, risks: string, category: string, novelty_score: number, implementation_notes: string, similar_concepts: string, idea_id: string}",
        "Idea: {idea_title}. Description: {idea_description}. Suggested category: {suggested_category}. Impact: {impact_estimate}. Effort: {effort_estimate}. Structure this into a formal idea proposal and assess novelty.",
        [
          { field: "structured_title", as: "structured_title", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "success_criteria", as: "success_criteria", type: "string" },
          { field: "risks", as: "idea_risks", type: "string" },
          { field: "category", as: "assigned_category", type: "string" },
          { field: "novelty_score", as: "novelty_score", type: "number" },
          {
            field: "implementation_notes",
            as: "implementation_notes",
            type: "string",
          },
          { field: "similar_concepts", as: "similar_concepts", type: "string" },
          { field: "idea_id", as: "idea_id", type: "string" },
        ],
      ),

      // 4. Duplicate similarity checker condition
      n("4", "condition", "Novelty Score Above Threshold (>5)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "novelty_score", operator: "gt", value: "5" },
          ],
        },
      }),

      // 5. Idea storage Sheets
      sheetsNode(
        "5",
        "Store Idea in Sheets",
        1000,
        "IdeaBank",
        '[["{idea_id}","{structured_title}","{assigned_category}","{submitter_name}","{submitter_email}","{value_proposition}","{novelty_score}","{impact_estimate}","{effort_estimate}","{priority}"]]',
      ),

      // 6. Auto-categorizer Slack
      slackNode(
        "6",
        "Notify Category Channel on Slack",
        1240,
        "New Idea Submitted: *{structured_title}*\\nCategory: {assigned_category} | Novelty: {novelty_score}/10\\nSubmitter: {submitter_name}\\n\\nValue Proposition: {value_proposition}\\n\\nTarget Audience: {target_audience}\\nImpact: {impact_estimate} | Effort: {effort_estimate}\\n\\nSimilar concepts: {similar_concepts}",
      ),

      // 7. Team notification email
      emailNode(
        "7",
        "Send Team Notification Email",
        1480,
        "{submitter_email}",
        "Idea Received: {structured_title} ({assigned_category})",
        "<h2>Idea Captured Successfully</h2><p>Hi {submitter_name},</p><p>Your idea <strong>{structured_title}</strong> has been received and categorized under <strong>{assigned_category}</strong>.</p><h3>Structured Proposal</h3><p><strong>Value Proposition:</strong> {value_proposition}</p><p><strong>Target Audience:</strong> {target_audience}</p><p><strong>Success Criteria:</strong> {success_criteria}</p><p><strong>Risks:</strong> {idea_risks}</p><p><strong>Novelty Score:</strong> {novelty_score}/10</p><p><strong>Implementation Notes:</strong> {implementation_notes}</p><p>Your idea ID is <strong>{idea_id}</strong> for future reference.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1477. LEARNING GOAL TRACKER (8 nodes)
  // ===================================================================

  "Learning Goal Tracker": {
    summary:
      "Receives learning goal updates via webhook, uses AI to extract goal data, designs a learning path with AI, aggregates resources via API, stores milestones in Sheets, waits for a weekly check-in delay, evaluates progress via condition, and celebrates milestones on Slack.",
    nodes: [
      trigger("Webhook: Learning goal submitted", "webhook_app", {
        source: "learning_platform",
        event: "learning_goal.created",
        howItStarts:
          "Fires when a user submits a new learning goal or updates progress — including the skill to learn, current level, target level, preferred learning style, and available time per week",
        setup:
          "Configure webhook from your learning management system or Slack command. Set OpenAI API key for path design. Provide course resource API credentials. Set Google Sheets spreadsheet ID for milestone tracking. Configure Slack webhook for celebrations.",
        inputFields: [],
      }),

      // 2. AI extract goal data
      aiNode(
        "2",
        "AI Extract Learning Goal Data",
        280,
        "You are a learning goal analyst. Parse the raw webhook payload to find the skill name, current proficiency level, target level, available hours per week, preferred learning style, deadline, learner name, learner email, and any specific topics of interest regardless of payload structure. Respond JSON: {skill_name: string, current_level: string, target_level: string, hours_per_week: number, learning_style: string, deadline: string, learner_name: string, learner_email: string, topics_of_interest: string, motivation: string}",
        "Raw webhook payload: {webhook_payload}. Extract all learning goal data regardless of field naming conventions.",
        [
          { field: "skill_name", as: "skill_name", type: "string" },
          { field: "current_level", as: "current_level", type: "string" },
          { field: "target_level", as: "target_level", type: "string" },
          { field: "hours_per_week", as: "hours_per_week", type: "number" },
          { field: "learning_style", as: "learning_style", type: "string" },
          { field: "deadline", as: "deadline", type: "string" },
          { field: "learner_name", as: "learner_name", type: "string" },
          { field: "learner_email", as: "learner_email", type: "string" },
          {
            field: "topics_of_interest",
            as: "topics_of_interest",
            type: "string",
          },
          { field: "motivation", as: "motivation", type: "string" },
        ],
      ),

      // 3. AI learning path designer
      aiNode(
        "3",
        "AI Design Personalized Learning Path",
        520,
        "You are an expert learning path designer. Given a skill goal with current and target levels, create a structured learning path with weekly milestones, recommended resources, practice exercises, and assessment checkpoints tailored to the learner's style and available time. Respond JSON: {learning_path: string, weekly_milestones: string, total_weeks: number, first_week_plan: string, resource_types: string, practice_exercises: string, assessment_schedule: string, difficulty_curve: string}",
        "Skill: {skill_name}. Current level: {current_level}. Target: {target_level}. Hours/week: {hours_per_week}. Style: {learning_style}. Topics: {topics_of_interest}. Deadline: {deadline}. Motivation: {motivation}. Design a complete learning path.",
        [
          { field: "learning_path", as: "learning_path", type: "string" },
          {
            field: "weekly_milestones",
            as: "weekly_milestones",
            type: "string",
          },
          { field: "total_weeks", as: "total_weeks", type: "number" },
          { field: "first_week_plan", as: "first_week_plan", type: "string" },
          { field: "resource_types", as: "resource_types", type: "string" },
          {
            field: "practice_exercises",
            as: "practice_exercises",
            type: "string",
          },
          {
            field: "assessment_schedule",
            as: "assessment_schedule",
            type: "string",
          },
          { field: "difficulty_curve", as: "difficulty_curve", type: "string" },
        ],
      ),

      // 4. Resource aggregator API
      n("4", "hitApi", "Search Learning Resources", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.udemy.com/api-2.0/courses/?search={skill_name}&ordering=relevance&page_size=5",
          headers: [
            { key: "Authorization", value: "Bearer {udemy_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "course_results", type: "string" },
            { name: "count", customName: "courses_found", type: "number" },
          ],
        },
      }),

      // 5. Goal milestone storage Sheets
      sheetsNode(
        "5",
        "Store Learning Goal and Milestones",
        1000,
        "LearningGoals",
        '[["{learner_name}","{skill_name}","{current_level}","{target_level}","{total_weeks}","{hours_per_week}","{learning_path}","{first_week_plan}","{deadline}","{courses_found}"]]',
      ),

      // 6. Weekly check-in delay
      n("6", "delayAction", "Wait 7 Days for Weekly Check-In", 1240, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 7,
          delayUnit: "days",
        },
      }),

      // 7. Progress evaluator condition
      n("7", "condition", "Learning Path Has Multiple Weeks?", 1480, {
        conditionDetails: {
          conditions: [{ variable: "total_weeks", operator: "gt", value: "1" }],
        },
      }),

      // 8. Milestone celebration Slack
      slackNode(
        "8",
        "Celebrate Learning Milestone on Slack",
        1720,
        "Learning Goal Update: *{skill_name}*\\nLearner: {learner_name}\\n\\nPath: {learning_path}\\nTotal weeks: {total_weeks}\\n\\nThis week's plan: {first_week_plan}\\n\\nResources found: {courses_found} courses\\n\\nPractice: {practice_exercises}\\n\\nKeep going! {motivation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1478. LEARNING JOURNAL BOT (7 nodes)
  // ===================================================================

  "Learning Journal Bot": {
    summary:
      "Receives learning entries via webhook, uses AI as a learning coach to enrich the entry, structures and tags entries with runCode, stores them in a Sheets knowledge base, schedules spaced repetition with runCode, handles review sessions via delay, and emails a monthly learning summary.",
    nodes: [
      trigger("Webhook: Learning entry submitted", "webhook_app", {
        source: "learning_journal",
        event: "entry.created",
        howItStarts:
          "Fires when a user logs a learning entry — including what they learned, source material, key takeaways, questions, and difficulty level — via Slack, web form, or API",
        setup:
          "Configure webhook from Slack or your journal app. Set OpenAI API key for AI learning coach. Provide Google Sheets spreadsheet ID for knowledge base. Configure email for monthly summaries.",
        inputFields: [],
      }),

      // 2. AI learning coach
      aiNode(
        "2",
        "AI Learning Coach",
        280,
        "You are an expert learning coach. Given a learning journal entry, enhance it with deeper insights, connections to prior knowledge, follow-up questions to explore, and practical applications. Also assess comprehension level and retention difficulty. Respond JSON: {enhanced_summary: string, key_insights: string, connections: string, follow_up_questions: string, practical_applications: string, comprehension_level: string, retention_difficulty: string, recommended_review_days: number, topic: string, subtopics: string, learner_email: string}",
        "Raw webhook payload: {webhook_payload}. Analyze this learning entry, enhance it with deeper insights, and suggest connections and applications.",
        [
          { field: "enhanced_summary", as: "enhanced_summary", type: "string" },
          { field: "key_insights", as: "key_insights", type: "string" },
          { field: "connections", as: "connections", type: "string" },
          {
            field: "follow_up_questions",
            as: "follow_up_questions",
            type: "string",
          },
          {
            field: "practical_applications",
            as: "practical_applications",
            type: "string",
          },
          {
            field: "comprehension_level",
            as: "comprehension_level",
            type: "string",
          },
          {
            field: "retention_difficulty",
            as: "retention_difficulty",
            type: "string",
          },
          {
            field: "recommended_review_days",
            as: "recommended_review_days",
            type: "number",
          },
          { field: "topic", as: "topic", type: "string" },
          { field: "subtopics", as: "subtopics", type: "string" },
          { field: "learner_email", as: "learner_email", type: "string" },
        ],
      ),

      // 3. Entry structurer tagger runCode
      n("3", "runCode", "Structure and Tag Learning Entry", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Structure the learning entry with tags and metadata
const topic = vars.topic || "General";
const subtopics = (vars.subtopics || "").split(",").map(s => s.trim()).filter(Boolean);
const comprehension = (vars.comprehension_level || "intermediate").toLowerCase();
const difficulty = (vars.retention_difficulty || "medium").toLowerCase();
const entryId = "LJ-" + Date.now().toString(36).toUpperCase();
const tags = [topic, ...subtopics, comprehension, difficulty].filter(Boolean);
const reviewPriority = difficulty === "high" ? "urgent" : difficulty === "medium" ? "normal" : "low";
const today = new Date().toISOString().split("T")[0];
return {
  entry_id: entryId,
  tags: tags.join(", "),
  entry_date: today,
  review_priority: reviewPriority,
  tag_count: tags.length,
  structured_entry: "ID: " + entryId + " | Topic: " + topic + " | Tags: " + tags.join(", ") + " | Comprehension: " + comprehension
};`,
          outputVariables: [
            { name: "entry_id", type: "string" },
            { name: "tags", type: "string" },
            { name: "entry_date", type: "string" },
            { name: "review_priority", type: "string" },
            { name: "tag_count", type: "number" },
            { name: "structured_entry", type: "string" },
          ],
        },
      }),

      // 4. Knowledge base storage Sheets
      sheetsNode(
        "4",
        "Store Entry in Knowledge Base",
        760,
        "KnowledgeBase",
        '[["{entry_id}","{entry_date}","{topic}","{tags}","{enhanced_summary}","{key_insights}","{comprehension_level}","{retention_difficulty}","{review_priority}","{follow_up_questions}"]]',
      ),

      // 5. Spaced repetition scheduler runCode
      n("5", "runCode", "Schedule Spaced Repetition Reviews", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate spaced repetition schedule based on difficulty
const reviewDays = parseInt(vars.recommended_review_days || "3");
const difficulty = (vars.retention_difficulty || "medium").toLowerCase();
const today = new Date();
// Fibonacci-like spacing for spaced repetition
const intervals = difficulty === "high" ? [1, 3, 7, 14, 30] :
  difficulty === "medium" ? [2, 5, 14, 30, 60] : [3, 7, 21, 60, 90];
const reviewDates = intervals.map(d => {
  const date = new Date(today.getTime() + d * 86400000);
  return date.toISOString().split("T")[0];
});
const nextReview = reviewDates[0];
const reviewSchedule = intervals.map((d, i) => "Review " + (i + 1) + ": " + reviewDates[i] + " (day " + d + ")").join("; ");
return {
  next_review_date: nextReview,
  review_schedule: reviewSchedule,
  total_reviews: intervals.length,
  first_interval_days: intervals[0],
  spaced_repetition_plan: "Topic: " + (vars.topic || "General") + " | Next review: " + nextReview + " | Total reviews: " + intervals.length
};`,
          outputVariables: [
            { name: "next_review_date", type: "string" },
            { name: "review_schedule", type: "string" },
            { name: "total_reviews", type: "number" },
            { name: "first_interval_days", type: "number" },
            { name: "spaced_repetition_plan", type: "string" },
          ],
        },
      }),

      // 6. Review session handler delay
      n("6", "delayAction", "Wait for First Review Date", 1240, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 3,
          delayUnit: "days",
        },
      }),

      // 7. Monthly learning summary email
      emailNode(
        "7",
        "Send Monthly Learning Summary Email",
        1480,
        "{learner_email}",
        "Learning Journal Review: {topic}",
        "<h2>Learning Journal — Review Reminder</h2><p>Entry: <strong>{entry_id}</strong> — {topic}</p><h3>Enhanced Summary</h3><p>{enhanced_summary}</p><h3>Key Insights</h3><p>{key_insights}</p><h3>Connections</h3><p>{connections}</p><h3>Follow-Up Questions</h3><p>{follow_up_questions}</p><h3>Practical Applications</h3><p>{practical_applications}</p><h3>Review Schedule</h3><p>{review_schedule}</p><p><em>Comprehension: {comprehension_level} | Difficulty: {retention_difficulty} | Priority: {review_priority}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1479. LEARNING PATH RECOMMENDER (8 nodes)
  // ===================================================================

  "Learning Path Recommender": {
    summary:
      "Receives a skill assessment via webhook, uses AI to extract skill data, analyzes gaps with runCode, searches course resources via API, optimizes the learning path with AI, personalizes recommendations with runCode, delivers the learning path via email, and integrates progress tracking in Sheets.",
    nodes: [
      trigger("Webhook: Skill assessment submitted", "webhook_app", {
        source: "skills_platform",
        event: "assessment.completed",
        howItStarts:
          "Fires when a user completes a skill assessment or requests learning path recommendations — including current skills, target role, experience level, time constraints, and learning preferences",
        setup:
          "Configure webhook from your skills assessment tool or HR platform. Set OpenAI API key for gap analysis and path optimization. Provide course API credentials. Configure email and Google Sheets for delivery and tracking.",
        inputFields: [],
      }),

      // 2. AI extract skill data
      aiNode(
        "2",
        "AI Extract Skill Assessment Data",
        280,
        "You are a skills assessment analyst. Parse the raw webhook payload to extract current skills with proficiency levels, target role or skill goals, experience years, learning budget, available hours per week, preferred formats, learner name, and email regardless of payload structure. Respond JSON: {learner_name: string, learner_email: string, current_skills: string, target_role: string, experience_years: number, learning_budget: number, hours_per_week: number, preferred_formats: string, career_goals: string, industry: string}",
        "Raw webhook payload: {webhook_payload}. Extract all skill assessment data regardless of field naming conventions.",
        [
          { field: "learner_name", as: "learner_name", type: "string" },
          { field: "learner_email", as: "learner_email", type: "string" },
          { field: "current_skills", as: "current_skills", type: "string" },
          { field: "target_role", as: "target_role", type: "string" },
          { field: "experience_years", as: "experience_years", type: "number" },
          { field: "learning_budget", as: "learning_budget", type: "number" },
          { field: "hours_per_week", as: "hours_per_week", type: "number" },
          {
            field: "preferred_formats",
            as: "preferred_formats",
            type: "string",
          },
          { field: "career_goals", as: "career_goals", type: "string" },
          { field: "industry", as: "industry", type: "string" },
        ],
      ),

      // 3. Skills gap analyzer runCode
      n("3", "runCode", "Analyze Skills Gap", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze skills gap between current skills and target role
const currentSkills = (vars.current_skills || "").split(",").map(s => s.trim()).filter(Boolean);
const targetRole = vars.target_role || "Senior Developer";
const experience = parseInt(vars.experience_years || "3");
// Role-based skill requirements mapping
const roleSkills = {
  "Senior Developer": ["System Design", "Code Review", "Mentoring", "Architecture", "Performance Optimization", "Testing Strategy"],
  "Tech Lead": ["Team Management", "Technical Strategy", "Stakeholder Communication", "Architecture", "Sprint Planning", "Code Review"],
  "Data Scientist": ["Machine Learning", "Statistics", "Python", "SQL", "Data Visualization", "Feature Engineering"],
  "Product Manager": ["User Research", "Roadmapping", "Analytics", "Stakeholder Management", "A/B Testing", "Prioritization"],
};
const requiredKey = Object.keys(roleSkills).find(k => targetRole.toLowerCase().includes(k.toLowerCase())) || "Senior Developer";
const required = roleSkills[requiredKey] || roleSkills["Senior Developer"];
const gaps = required.filter(r => !currentSkills.some(c => c.toLowerCase().includes(r.toLowerCase())));
const strengths = currentSkills.filter(c => required.some(r => r.toLowerCase().includes(c.toLowerCase())));
const gapScore = Math.round((gaps.length / required.length) * 100);
return {
  skill_gaps: gaps.join(", "),
  existing_strengths: strengths.join(", "),
  gap_count: gaps.length,
  gap_percentage: gapScore,
  required_skills: required.join(", "),
  priority_skills: gaps.slice(0, 3).join(", "),
  readiness_score: 100 - gapScore
};`,
          outputVariables: [
            { name: "skill_gaps", type: "string" },
            { name: "existing_strengths", type: "string" },
            { name: "gap_count", type: "number" },
            { name: "gap_percentage", type: "number" },
            { name: "required_skills", type: "string" },
            { name: "priority_skills", type: "string" },
            { name: "readiness_score", type: "number" },
          ],
        },
      }),

      // 4. Course resource search API
      n("4", "hitApi", "Search Course Resources", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.udemy.com/api-2.0/courses/?search={priority_skills}&ordering=relevance&page_size=10&price=price-paid",
          headers: [
            { key: "Authorization", value: "Bearer {udemy_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "course_results", type: "string" },
            { name: "count", customName: "total_courses", type: "number" },
          ],
        },
      }),

      // 5. AI path optimizer
      aiNode(
        "5",
        "AI Optimize Learning Path",
        1000,
        "You are a learning path optimization expert. Given skill gaps, available courses, budget, and time constraints, create an optimized learning path that maximizes skill acquisition efficiency. Prioritize by impact, sequence dependencies, and learner preferences. Respond JSON: {optimized_path: string, phase_1_skills: string, phase_2_skills: string, phase_3_skills: string, total_estimated_hours: number, estimated_completion_weeks: number, recommended_courses: string, path_rationale: string}",
        "Learner: {learner_name}. Target: {target_role}. Gaps: {skill_gaps}. Strengths: {existing_strengths}. Budget: ${learning_budget}. Hours/week: {hours_per_week}. Formats: {preferred_formats}. Courses found: {course_results}. Industry: {industry}. Optimize the learning path.",
        [
          { field: "optimized_path", as: "optimized_path", type: "string" },
          { field: "phase_1_skills", as: "phase_1_skills", type: "string" },
          { field: "phase_2_skills", as: "phase_2_skills", type: "string" },
          { field: "phase_3_skills", as: "phase_3_skills", type: "string" },
          {
            field: "total_estimated_hours",
            as: "total_estimated_hours",
            type: "number",
          },
          {
            field: "estimated_completion_weeks",
            as: "estimated_completion_weeks",
            type: "number",
          },
          {
            field: "recommended_courses",
            as: "recommended_courses",
            type: "string",
          },
          { field: "path_rationale", as: "path_rationale", type: "string" },
        ],
      ),

      // 6. Personalization engine runCode
      n("6", "runCode", "Personalize Learning Recommendations", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Personalize the path based on learner preferences and constraints
const hoursPerWeek = parseInt(vars.hours_per_week || "5");
const budget = parseInt(vars.learning_budget || "500");
const totalHours = parseInt(vars.total_estimated_hours || "100");
const weeks = parseInt(vars.estimated_completion_weeks || "20");
const formats = (vars.preferred_formats || "video").toLowerCase();
const weeklyPlan = Math.min(hoursPerWeek, Math.ceil(totalHours / weeks));
const costPerWeek = Math.round(budget / weeks);
const formatRecommendations = formats.includes("video") ? "Prioritize video courses and tutorials" :
  formats.includes("book") ? "Start with foundational books, supplement with hands-on labs" :
  formats.includes("project") ? "Focus on project-based learning with mentorship" :
  "Mixed format approach with video and hands-on exercises";
const learningId = "LP-" + Date.now().toString(36).toUpperCase();
return {
  learning_plan_id: learningId,
  weekly_hours_plan: weeklyPlan,
  weekly_budget: costPerWeek,
  format_recommendation: formatRecommendations,
  personalized_note: "Customized for " + hoursPerWeek + "h/week budget with " + formats + " preference",
  start_date: new Date().toISOString().split("T")[0],
  target_completion: new Date(Date.now() + weeks * 7 * 86400000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "learning_plan_id", type: "string" },
            { name: "weekly_hours_plan", type: "number" },
            { name: "weekly_budget", type: "number" },
            { name: "format_recommendation", type: "string" },
            { name: "personalized_note", type: "string" },
            { name: "start_date", type: "string" },
            { name: "target_completion", type: "string" },
          ],
        },
      }),

      // 7. Learning path delivery email
      emailNode(
        "7",
        "Email Personalized Learning Path",
        1480,
        "{learner_email}",
        "Your Learning Path to {target_role}: {learning_plan_id}",
        "<h2>Your Personalized Learning Path</h2><p>Hi {learner_name},</p><p>Here's your customized path to becoming a <strong>{target_role}</strong>.</p><h3>Readiness Score: {readiness_score}%</h3><p>Skills to develop: {skill_gaps}</p><p>Existing strengths: {existing_strengths}</p><h3>Learning Path</h3><p>{optimized_path}</p><h3>Phases</h3><ol><li>{phase_1_skills}</li><li>{phase_2_skills}</li><li>{phase_3_skills}</li></ol><h3>Schedule</h3><p>{weekly_hours_plan} hours/week | Est. completion: {target_completion}</p><p>{format_recommendation}</p><h3>Recommended Courses</h3><p>{recommended_courses}</p><p><em>{path_rationale}</em></p><p>Plan ID: {learning_plan_id} | Start: {start_date}</p>",
      ),

      // 8. Progress integration Sheets
      sheetsNode(
        "8",
        "Log Learning Path in Sheets",
        1720,
        "LearningPaths",
        '[["{learning_plan_id}","{learner_name}","{target_role}","{gap_count}","{readiness_score}","{total_estimated_hours}","{estimated_completion_weeks}","{start_date}","{target_completion}","{priority_skills}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1480. MENU ALLERGEN INFORMATION BOT (8 nodes)
  // ===================================================================

  "Menu Allergen Information Bot": {
    summary:
      "Receives allergen queries via webhook, uses AI to extract the allergen profile, queries the menu database API, analyzes allergens with AI, filters safe menu items via condition, generates modification suggestions with AI, places the order with allergen notes via API, and confirms to the kitchen on Slack.",
    nodes: [
      trigger("Webhook: Allergen query received", "webhook_app", {
        source: "restaurant_chat",
        event: "allergen.query",
        howItStarts:
          "Fires when a customer asks about allergens or dietary restrictions — via chat, kiosk, or app — including their allergen profile, food preferences, and what they're interested in ordering",
        setup:
          "Configure webhook from your restaurant ordering system or chat platform. Set OpenAI API key for allergen analysis. Provide menu database API credentials. Set Slack webhook URL for kitchen notifications.",
        inputFields: [],
      }),

      // 2. AI extract allergen profile
      aiNode(
        "2",
        "AI Extract Allergen Profile",
        280,
        "You are a dietary restriction specialist. Parse the raw webhook payload to extract the customer's allergen list, dietary restrictions (vegan, halal, kosher, etc.), food preferences, items they want to order, severity level of allergies, customer name, and contact info regardless of payload format. Respond JSON: {customer_name: string, customer_email: string, allergens: string, dietary_type: string, severity_level: string, items_interested: string, food_preferences: string, table_number: string, order_type: string, special_notes: string}",
        "Raw webhook payload: {webhook_payload}. Extract all allergen and dietary information regardless of field naming conventions.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "allergens", as: "allergens", type: "string" },
          { field: "dietary_type", as: "dietary_type", type: "string" },
          { field: "severity_level", as: "severity_level", type: "string" },
          { field: "items_interested", as: "items_interested", type: "string" },
          { field: "food_preferences", as: "food_preferences", type: "string" },
          { field: "table_number", as: "table_number", type: "string" },
          { field: "order_type", as: "order_type", type: "string" },
          { field: "special_notes", as: "special_notes", type: "string" },
        ],
      ),

      // 3. Menu database query API
      n("3", "hitApi", "Query Menu Database with Allergen Filters", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.restaurant.com/v1/menu?include_allergens=true&dietary={dietary_type}",
          headers: [
            { key: "Authorization", value: "Bearer {restaurant_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "menu.items", customName: "full_menu", type: "string" },
            {
              name: "menu.allergen_index",
              customName: "allergen_index",
              type: "string",
            },
            {
              name: "menu.item_count",
              customName: "menu_item_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI allergen analyzer
      aiNode(
        "4",
        "AI Analyze Allergens in Menu Items",
        760,
        "You are a food safety and allergen expert. Cross-reference the customer's allergen profile against the full menu with ingredient data. Identify safe items, items with allergens, items that can be modified to be safe, and items that are absolutely off-limits. Consider cross-contamination risks for severe allergies. Respond JSON: {safe_items: string, unsafe_items: string, modifiable_items: string, cross_contamination_risks: string, safe_item_count: number, allergen_match_details: string, safety_rating: string, all_items_safe: string}",
        "Customer allergens: {allergens}. Severity: {severity_level}. Dietary type: {dietary_type}. Items interested in: {items_interested}. Full menu: {full_menu}. Allergen index: {allergen_index}. Analyze allergen safety for all menu items.",
        [
          { field: "safe_items", as: "safe_items", type: "string" },
          { field: "unsafe_items", as: "unsafe_items", type: "string" },
          { field: "modifiable_items", as: "modifiable_items", type: "string" },
          {
            field: "cross_contamination_risks",
            as: "cross_contamination_risks",
            type: "string",
          },
          { field: "safe_item_count", as: "safe_item_count", type: "number" },
          {
            field: "allergen_match_details",
            as: "allergen_match_details",
            type: "string",
          },
          { field: "safety_rating", as: "safety_rating", type: "string" },
          { field: "all_items_safe", as: "all_items_safe", type: "string" },
        ],
      ),

      // 5. Safe menu filter condition
      n("5", "condition", "Safe Items Available?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "safe_item_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 6. Modification suggester AI
      aiNode(
        "6",
        "AI Suggest Menu Modifications",
        1240,
        "You are a creative chef and dietary modification specialist. For items that can be modified to be allergen-safe, provide specific modification instructions. Also suggest the best safe alternatives from the menu that match the customer's preferences. Respond JSON: {modification_instructions: string, best_alternatives: string, chef_notes: string, recommended_order: string, estimated_price: string, allergen_disclaimer: string}",
        "Customer: {customer_name}. Allergens: {allergens}. Severity: {severity_level}. Preferences: {food_preferences}. Safe items: {safe_items}. Modifiable items: {modifiable_items}. Cross-contamination risks: {cross_contamination_risks}. Special notes: {special_notes}. Suggest modifications and alternatives.",
        [
          {
            field: "modification_instructions",
            as: "modification_instructions",
            type: "string",
          },
          {
            field: "best_alternatives",
            as: "best_alternatives",
            type: "string",
          },
          { field: "chef_notes", as: "chef_notes", type: "string" },
          {
            field: "recommended_order",
            as: "recommended_order",
            type: "string",
          },
          { field: "estimated_price", as: "estimated_price", type: "string" },
          {
            field: "allergen_disclaimer",
            as: "allergen_disclaimer",
            type: "string",
          },
        ],
      ),

      // 7. Order placement with allergen notes API
      n("7", "hitApi", "Place Order with Allergen Notes", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.restaurant.com/v1/orders",
          headers: [
            { key: "Authorization", value: "Bearer {restaurant_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "customer_name", value: "{customer_name}" },
            { key: "items", value: "{recommended_order}" },
            {
              key: "allergen_notes",
              value:
                "ALLERGENS: {allergens} (Severity: {severity_level}). Modifications: {modification_instructions}. Chef notes: {chef_notes}",
            },
            { key: "table_number", value: "{table_number}" },
            { key: "order_type", value: "{order_type}" },
            { key: "dietary_type", value: "{dietary_type}" },
          ],
          variables: [
            { name: "order_id", customName: "order_id", type: "string" },
            { name: "status", customName: "order_status", type: "string" },
          ],
        },
      }),

      // 8. Kitchen confirmation Slack
      slackNode(
        "8",
        "Alert Kitchen About Allergen Order",
        1720,
        "ALLERGEN ALERT — Order {order_id} | Table {table_number}\\n\\nCustomer: {customer_name}\\nAllergens: *{allergens}* (Severity: {severity_level})\\nDietary: {dietary_type}\\n\\nOrder: {recommended_order}\\n\\nModifications: {modification_instructions}\\nChef Notes: {chef_notes}\\nCross-contamination risks: {cross_contamination_risks}\\n\\n{allergen_disclaimer}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1481. MORNING ROUTINE BOT (8 nodes)
  // ===================================================================

  "Morning Routine Bot": {
    summary:
      "Runs on a morning schedule to fetch weather and commute data via API, preview the day's calendar via Google Calendar, generate an AI morning brief, deliver a routine checklist via email, compile a news and industry digest with runCode, track routine completion on Slack, and log optimization data to Sheets.",
    nodes: [
      trigger("Schedule: Morning routine brief", "schedule", {
        schedule: "Every weekday at 6:30 AM",
        howItStarts:
          "Runs every weekday morning to compile weather, commute, calendar, and news into a personalized morning brief, track routine completion, and continuously optimize the routine",
        setup:
          "Provide weather API key (OpenWeatherMap). Provide Google Calendar and Maps API credentials. Set OpenAI API key for brief generation. Configure email for delivery. Set Slack webhook and Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Weather commute fetcher API
      n("2", "hitApi", "Fetch Weather and Commute Data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.openweathermap.org/data/2.5/weather?q={user_city}&appid={weather_api_key}&units=imperial",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "main.temp", customName: "temperature", type: "number" },
            {
              name: "weather[0].description",
              customName: "weather_description",
              type: "string",
            },
            { name: "main.humidity", customName: "humidity", type: "number" },
            { name: "wind.speed", customName: "wind_speed", type: "number" },
            { name: "name", customName: "city_name", type: "string" },
          ],
        },
      }),

      // 3. Calendar preview Google Calendar API
      n("3", "hitApi", "Fetch Today's Calendar Events", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={today_start}&timeMax={today_end}&singleEvents=true&orderBy=startTime&maxResults=10",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "today_events", type: "string" },
            { name: "summary", customName: "calendar_summary", type: "string" },
          ],
        },
      }),

      // 4. AI morning brief generator
      aiNode(
        "4",
        "AI Generate Morning Brief",
        760,
        "You are a personal assistant creating a concise morning brief. Combine weather, commute estimates, calendar events, and any relevant context into a clear, actionable morning summary. Include outfit suggestions based on weather, meeting prep reminders, and time-optimized routine suggestions. Respond JSON: {morning_brief: string, outfit_suggestion: string, meeting_prep_notes: string, time_blocks: string, daily_focus: string, commute_recommendation: string, routine_checklist: string, event_count: number}",
        "Weather: {temperature}F, {weather_description}, humidity {humidity}%, wind {wind_speed}mph in {city_name}. Calendar: {today_events}. Generate a comprehensive morning brief.",
        [
          { field: "morning_brief", as: "morning_brief", type: "string" },
          {
            field: "outfit_suggestion",
            as: "outfit_suggestion",
            type: "string",
          },
          {
            field: "meeting_prep_notes",
            as: "meeting_prep_notes",
            type: "string",
          },
          { field: "time_blocks", as: "time_blocks", type: "string" },
          { field: "daily_focus", as: "daily_focus", type: "string" },
          {
            field: "commute_recommendation",
            as: "commute_recommendation",
            type: "string",
          },
          {
            field: "routine_checklist",
            as: "routine_checklist",
            type: "string",
          },
          { field: "event_count", as: "event_count", type: "number" },
        ],
      ),

      // 5. Routine checklist deliverer email
      emailNode(
        "5",
        "Deliver Morning Brief via Email",
        1000,
        "{user_email}",
        "Good Morning! Your Daily Brief — {city_name} {temperature}F",
        "<h2>Morning Brief</h2><p>{morning_brief}</p><h3>Weather</h3><p>{temperature}F, {weather_description} | Humidity: {humidity}% | Wind: {wind_speed}mph</p><p>Outfit: {outfit_suggestion}</p><h3>Today's Calendar ({event_count} events)</h3><p>{time_blocks}</p><h3>Meeting Prep</h3><p>{meeting_prep_notes}</p><h3>Daily Focus</h3><p>{daily_focus}</p><h3>Commute</h3><p>{commute_recommendation}</p><h3>Routine Checklist</h3><p>{routine_checklist}</p>",
      ),

      // 6. News industry digest runCode
      n("6", "runCode", "Compile News and Industry Digest", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile a quick industry digest with key metrics
const today = new Date();
const dayOfWeek = today.toLocaleDateString("en-US", { weekday: "long" });
const dateStr = today.toISOString().split("T")[0];
const eventCount = parseInt(vars.event_count || "0");
const temp = parseInt(vars.temperature || "70");
const briefLength = (vars.morning_brief || "").length;
// Build routine metrics
const routineScore = eventCount < 3 ? "light" : eventCount < 6 ? "moderate" : "packed";
const weatherAlert = temp < 32 ? "Freezing — dress warmly" : temp > 95 ? "Extreme heat — stay hydrated" : "normal";
const focusPotential = eventCount < 4 ? "high" : eventCount < 7 ? "medium" : "low";
return {
  digest_date: dateStr,
  day_of_week: dayOfWeek,
  day_type: routineScore,
  weather_alert: weatherAlert,
  focus_potential: focusPotential,
  routine_metrics: "Day type: " + routineScore + " | Events: " + eventCount + " | Focus potential: " + focusPotential + " | Weather: " + weatherAlert,
  optimization_note: focusPotential === "high" ? "Great day for deep work — block 2+ hours" : "Meeting-heavy day — batch shallow tasks between meetings"
};`,
          outputVariables: [
            { name: "digest_date", type: "string" },
            { name: "day_of_week", type: "string" },
            { name: "day_type", type: "string" },
            { name: "weather_alert", type: "string" },
            { name: "focus_potential", type: "string" },
            { name: "routine_metrics", type: "string" },
            { name: "optimization_note", type: "string" },
          ],
        },
      }),

      // 7. Routine completion tracker Slack
      slackNode(
        "7",
        "Post Routine Summary to Slack",
        1480,
        "Morning Routine — {day_of_week} {digest_date}\\n\\n{routine_metrics}\\n\\nDaily Focus: {daily_focus}\\nOptimization: {optimization_note}\\n\\nChecklist: {routine_checklist}",
      ),

      // 8. Routine optimization Sheets
      sheetsNode(
        "8",
        "Log Routine Data to Sheets",
        1720,
        "RoutineLog",
        '[["{digest_date}","{day_of_week}","{temperature}","{weather_description}","{event_count}","{day_type}","{focus_potential}","{daily_focus}","{weather_alert}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1482. PERSONAL FINANCE TRACKER (9 nodes)
  // ===================================================================

  "Personal Finance Tracker": {
    summary:
      "Receives transaction data via webhook, uses AI to extract transaction details, categorizes with runCode, compares against budgets with runCode, checks budget thresholds via condition, sends spending alerts via email, detects anomalies via condition, tracks recurring expenses in Sheets, and emails a weekly financial summary.",
    nodes: [
      trigger("Webhook: Transaction recorded", "webhook_app", {
        source: "banking_platform",
        event: "transaction.created",
        howItStarts:
          "Fires when a new transaction is recorded — bank notification, credit card charge, or manual entry — including amount, merchant, date, and payment method",
        setup:
          "Configure webhook from your banking notification system (Plaid, bank email parser, or manual entry form). Set OpenAI API key for extraction. Configure email for alerts. Provide Google Sheets spreadsheet ID for expense tracking.",
        inputFields: [],
      }),

      // 2. AI extract transaction data
      aiNode(
        "2",
        "AI Extract Transaction Data",
        280,
        "You are a financial data parser. Extract the transaction amount, merchant name, date, category hint, payment method, account, description, and any recurring indicators from the raw webhook payload regardless of format. Respond JSON: {amount: number, merchant_name: string, transaction_date: string, category_hint: string, payment_method: string, account_name: string, description: string, is_recurring_hint: string, currency: string, user_email: string}",
        "Raw webhook payload: {webhook_payload}. Extract all transaction data regardless of field naming conventions.",
        [
          { field: "amount", as: "amount", type: "number" },
          { field: "merchant_name", as: "merchant_name", type: "string" },
          { field: "transaction_date", as: "transaction_date", type: "string" },
          { field: "category_hint", as: "category_hint", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "description", as: "description", type: "string" },
          {
            field: "is_recurring_hint",
            as: "is_recurring_hint",
            type: "string",
          },
          { field: "currency", as: "currency", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
        ],
      ),

      // 3. Transaction categorizer runCode
      n("3", "runCode", "Categorize Transaction", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Categorize transaction based on merchant and hints
const merchant = (vars.merchant_name || "").toLowerCase();
const hint = (vars.category_hint || "").toLowerCase();
const amount = parseFloat(vars.amount || "0");
const categoryMap = {
  grocery: ["walmart", "kroger", "safeway", "whole foods", "trader joe", "costco", "grocery", "food"],
  dining: ["restaurant", "mcdonald", "starbucks", "chipotle", "uber eats", "doordash", "grubhub", "cafe"],
  transport: ["uber", "lyft", "gas", "shell", "chevron", "parking", "transit", "metro"],
  utilities: ["electric", "water", "internet", "phone", "comcast", "verizon", "att"],
  entertainment: ["netflix", "spotify", "hulu", "disney", "movie", "game", "concert"],
  shopping: ["amazon", "target", "ebay", "mall", "clothing", "shoes"],
  health: ["pharmacy", "cvs", "walgreens", "doctor", "hospital", "dental", "gym"],
  subscription: ["subscription", "membership", "premium", "monthly", "annual"],
};
let category = "other";
for (const [cat, keywords] of Object.entries(categoryMap)) {
  if (keywords.some(k => merchant.includes(k) || hint.includes(k))) {
    category = cat;
    break;
  }
}
const transactionId = "TXN-" + Date.now().toString(36).toUpperCase();
const isRecurring = (vars.is_recurring_hint || "").toLowerCase() === "yes" || category === "subscription" || category === "utilities";
return {
  transaction_id: transactionId,
  category: category,
  is_recurring: isRecurring ? "yes" : "no",
  categorization_confidence: category !== "other" ? "high" : "low",
  amount_formatted: "$" + amount.toFixed(2)
};`,
          outputVariables: [
            { name: "transaction_id", type: "string" },
            { name: "category", type: "string" },
            { name: "is_recurring", type: "string" },
            { name: "categorization_confidence", type: "string" },
            { name: "amount_formatted", type: "string" },
          ],
        },
      }),

      // 4. Budget comparator runCode
      n("4", "runCode", "Compare Against Monthly Budget", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare transaction against category budget
const category = vars.category || "other";
const amount = parseFloat(vars.amount || "0");
const budgets = {
  grocery: 600, dining: 300, transport: 200, utilities: 250,
  entertainment: 150, shopping: 400, health: 200, subscription: 100, other: 300
};
const monthlyBudget = budgets[category] || 300;
// Simulate month-to-date spending (in production, query from database)
const dayOfMonth = new Date().getDate();
const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
const projectedMonthly = (amount * (daysInMonth / dayOfMonth));
const budgetUsed = Math.round((projectedMonthly / monthlyBudget) * 100);
const overBudget = budgetUsed > 90;
const isAnomaly = amount > monthlyBudget * 0.3;
return {
  monthly_budget: monthlyBudget,
  budget_used_percent: budgetUsed,
  over_budget: overBudget ? "yes" : "no",
  is_anomaly: isAnomaly ? "yes" : "no",
  budget_remaining: Math.max(0, monthlyBudget - projectedMonthly).toFixed(2),
  budget_status: overBudget ? "warning" : budgetUsed > 70 ? "caution" : "healthy",
  anomaly_reason: isAnomaly ? "Single transaction exceeds 30% of " + category + " budget ($" + monthlyBudget + ")" : "normal"
};`,
          outputVariables: [
            { name: "monthly_budget", type: "number" },
            { name: "budget_used_percent", type: "number" },
            { name: "over_budget", type: "string" },
            { name: "is_anomaly", type: "string" },
            { name: "budget_remaining", type: "string" },
            { name: "budget_status", type: "string" },
            { name: "anomaly_reason", type: "string" },
          ],
        },
      }),

      // 5. Threshold alert engine condition
      n("5", "condition", "Budget Over 90% Used?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "over_budget", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Spending alert email (true path)
      emailNode(
        "6",
        "Send Budget Alert Email",
        1240,
        "{user_email}",
        "Budget Alert: {category} at {budget_used_percent}%",
        "<h2>Budget Alert</h2><p>Your <strong>{category}</strong> spending is at <strong>{budget_used_percent}%</strong> of your ${monthly_budget} monthly budget.</p><p>Latest transaction: {amount_formatted} at {merchant_name}</p><p>Budget remaining: ${budget_remaining}</p><p>Status: {budget_status}</p>",
        300,
      ),

      // 7. Anomaly detector condition
      n("7", "condition", "Anomalous Transaction Detected?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_anomaly", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Recurring expense tracker Sheets
      sheetsNode(
        "8",
        "Log Transaction to Expense Tracker",
        1720,
        "Transactions",
        '[["{transaction_id}","{transaction_date}","{merchant_name}","{amount}","{category}","{payment_method}","{is_recurring}","{budget_used_percent}","{budget_status}","{is_anomaly}"]]',
      ),

      // 9. Weekly financial summary email
      emailNode(
        "9",
        "Send Anomaly Alert Email",
        1960,
        "{user_email}",
        "Transaction Anomaly Detected: {amount_formatted} at {merchant_name}",
        "<h2>Anomaly Alert</h2><p>A potentially unusual transaction was detected:</p><p><strong>{amount_formatted}</strong> at <strong>{merchant_name}</strong></p><p>Category: {category}</p><p>Reason: {anomaly_reason}</p><p>If this was not you, please review your account immediately.</p>",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "9", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1483. POMODORO TIMER WITH TASK LOGGER (8 nodes)
  // ===================================================================

  "Pomodoro Timer with Task Logger": {
    summary:
      "Receives a timer start command via webhook, manages session state with runCode, uses a delay for the focus interval, logs break notifications to Slack, updates task progress in Asana API, logs interruptions with runCode, generates a session summary with runCode, and emails a daily pomodoro report.",
    nodes: [
      trigger("Webhook: Pomodoro timer start", "webhook_app", {
        source: "pomodoro_app",
        event: "timer.started",
        howItStarts:
          "Fires when a user starts a Pomodoro session — via Slack command, web app, or keyboard shortcut — including the task name, project, estimated pomodoros, and any notes",
        setup:
          "Configure webhook from your Pomodoro app or Slack slash command. Set Asana API credentials for task progress updates. Set Slack webhook URL for break notifications. Configure email for daily reports.",
        inputFields: [],
      }),

      // 2. Session state manager runCode
      n("2", "runCode", "Initialize Pomodoro Session State", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and initialize Pomodoro session
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const taskName = parsed.task || parsed.task_name || parsed.text || "Focus task";
const projectName = parsed.project || parsed.project_name || "General";
const estimatedPomodoros = parseInt(parsed.pomodoros || parsed.estimated || "4");
const pomodoroMinutes = parseInt(parsed.duration || "25");
const breakMinutes = parseInt(parsed.break_time || "5");
const longBreakMinutes = parseInt(parsed.long_break || "15");
const asanaTaskId = parsed.asana_task_id || parsed.task_id || "";
const sessionId = "POM-" + Date.now().toString(36).toUpperCase();
const userEmail = parsed.email || parsed.user_email || "";
return {
  session_id: sessionId,
  task_name: taskName,
  project_name: projectName,
  estimated_pomodoros: estimatedPomodoros,
  pomodoro_minutes: pomodoroMinutes,
  break_minutes: breakMinutes,
  long_break_minutes: longBreakMinutes,
  asana_task_id: asanaTaskId,
  session_start: new Date().toISOString(),
  user_email: userEmail
};`,
          outputVariables: [
            { name: "session_id", type: "string" },
            { name: "task_name", type: "string" },
            { name: "project_name", type: "string" },
            { name: "estimated_pomodoros", type: "number" },
            { name: "pomodoro_minutes", type: "number" },
            { name: "break_minutes", type: "number" },
            { name: "long_break_minutes", type: "number" },
            { name: "asana_task_id", type: "string" },
            { name: "session_start", type: "string" },
            { name: "user_email", type: "string" },
          ],
        },
      }),

      // 3. Focus interval timer delay
      n("3", "delayAction", "Wait for Pomodoro Focus Interval", 520, {
        delayActionDetails: {
          delayType: "duration",
          delayDuration: 25,
          delayUnit: "minutes",
        },
      }),

      // 4. Break notification logger Slack
      slackNode(
        "4",
        "Send Break Notification to Slack",
        760,
        "Pomodoro Complete! *{task_name}* ({project_name})\\nSession: {session_id}\\n\\nGreat work! Take a {break_minutes}-minute break.\\n\\nEstimated pomodoros: {estimated_pomodoros}\\n\\nRemember to stretch, hydrate, and rest your eyes.",
      ),

      // 5. Task progress updater Asana API
      n("5", "hitApi", "Update Task Progress in Asana", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://app.asana.com/api/1.0/tasks/{asana_task_id}",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"custom_fields":{"pomodoros_completed":"1","last_pomodoro_session":"{session_id}"},"notes":"Pomodoro session {session_id} completed for {task_name}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "updated_task_id", type: "string" },
            {
              name: "data.modified_at",
              customName: "task_modified_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Interruption logger runCode
      n("6", "runCode", "Log Session Interruptions", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Log any interruptions and calculate focus quality
const sessionStart = vars.session_start || new Date().toISOString();
const pomodoroMin = parseInt(vars.pomodoro_minutes || "25");
const now = new Date();
const startTime = new Date(sessionStart);
const elapsedMin = Math.round((now.getTime() - startTime.getTime()) / 60000);
const overtime = elapsedMin - pomodoroMin;
const focusQuality = overtime <= 1 ? "excellent" : overtime <= 5 ? "good" : "interrupted";
const interruptionCount = Math.max(0, Math.floor(overtime / 5));
return {
  elapsed_minutes: elapsedMin,
  overtime_minutes: Math.max(0, overtime),
  focus_quality: focusQuality,
  interruption_count: interruptionCount,
  interruption_log: interruptionCount > 0 ? interruptionCount + " likely interruptions detected (overtime: " + overtime + " min)" : "No interruptions — perfect focus session",
  pomodoro_completed: "1"
};`,
          outputVariables: [
            { name: "elapsed_minutes", type: "number" },
            { name: "overtime_minutes", type: "number" },
            { name: "focus_quality", type: "string" },
            { name: "interruption_count", type: "number" },
            { name: "interruption_log", type: "string" },
            { name: "pomodoro_completed", type: "string" },
          ],
        },
      }),

      // 7. Session summary generator runCode
      n("7", "runCode", "Generate Pomodoro Session Summary", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate comprehensive session summary
const taskName = vars.task_name || "Focus task";
const project = vars.project_name || "General";
const focusQuality = vars.focus_quality || "good";
const elapsed = parseInt(vars.elapsed_minutes || "25");
const interruptions = parseInt(vars.interruption_count || "0");
const sessionId = vars.session_id || "";
const pomodorosEst = parseInt(vars.estimated_pomodoros || "4");
const productivityScore = focusQuality === "excellent" ? 100 :
  focusQuality === "good" ? 85 : 70 - (interruptions * 5);
const summary = [
  "Session: " + sessionId,
  "Task: " + taskName + " (" + project + ")",
  "Duration: " + elapsed + " minutes",
  "Focus quality: " + focusQuality,
  "Interruptions: " + interruptions,
  "Productivity score: " + productivityScore + "/100",
  "Progress: 1/" + pomodorosEst + " pomodoros"
].join("\\n");
return {
  session_summary: summary,
  productivity_score: productivityScore,
  session_date: new Date().toISOString().split("T")[0],
  session_end: new Date().toISOString(),
  total_focus_minutes: elapsed
};`,
          outputVariables: [
            { name: "session_summary", type: "string" },
            { name: "productivity_score", type: "number" },
            { name: "session_date", type: "string" },
            { name: "session_end", type: "string" },
            { name: "total_focus_minutes", type: "number" },
          ],
        },
      }),

      // 8. Daily pomodoro report email
      emailNode(
        "8",
        "Send Daily Pomodoro Report Email",
        1720,
        "{user_email}",
        "Pomodoro Report: {task_name} — Score {productivity_score}/100",
        "<h2>Pomodoro Session Report</h2><pre>{session_summary}</pre><h3>Focus Quality</h3><p>{focus_quality} — {interruption_log}</p><h3>Task Progress</h3><p>Task: {task_name} ({project_name})</p><p>Pomodoros: 1/{estimated_pomodoros} completed</p><p>Asana task updated: {updated_task_id}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1484. READ LATER ARTICLE SUMMARIZER (7 nodes)
  // ===================================================================

  "Read Later Article Summarizer": {
    summary:
      "Receives saved articles via webhook, extracts content with runCode, generates multi-level AI summaries, tags and scores priorities with runCode, manages the reading queue in Sheets, sends a daily reading digest email, and cleans up stale articles via condition.",
    nodes: [
      trigger("Webhook: Article saved for later", "webhook_app", {
        source: "read_later_app",
        event: "article.saved",
        howItStarts:
          "Fires when a user saves an article for later reading — via browser extension, Pocket, Instapaper, or API — including the URL, title, and any initial tags",
        setup:
          "Configure webhook from your read-later service (Pocket, Instapaper, or custom browser extension). Set OpenAI API key for summarization. Provide Google Sheets spreadsheet ID for queue management. Configure email for daily digest.",
        inputFields: [],
      }),

      // 2. Content extractor runCode
      n("2", "runCode", "Extract Article Content and Metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload to extract article metadata
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const url = parsed.url || parsed.article_url || parsed.link || "";
const title = parsed.title || parsed.article_title || "Untitled Article";
const source = parsed.source || parsed.domain || url.split("/")[2] || "unknown";
const tags = parsed.tags || parsed.labels || "";
const savedBy = parsed.user || parsed.email || parsed.saved_by || "";
const wordCount = parseInt(parsed.word_count || parsed.length || "1500");
const readTimeMin = Math.ceil(wordCount / 250);
const articleId = "ART-" + Date.now().toString(36).toUpperCase();
return {
  article_id: articleId,
  article_url: url,
  article_title: title,
  article_source: source,
  initial_tags: tags,
  saved_by_email: savedBy,
  estimated_word_count: wordCount,
  read_time_minutes: readTimeMin,
  saved_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "article_id", type: "string" },
            { name: "article_url", type: "string" },
            { name: "article_title", type: "string" },
            { name: "article_source", type: "string" },
            { name: "initial_tags", type: "string" },
            { name: "saved_by_email", type: "string" },
            { name: "estimated_word_count", type: "number" },
            { name: "read_time_minutes", type: "number" },
            { name: "saved_date", type: "string" },
          ],
        },
      }),

      // 3. AI multi-level summarizer
      aiNode(
        "3",
        "AI Generate Multi-Level Summary",
        520,
        "You are an expert article summarizer. Create three levels of summary: a one-sentence TL;DR, a 3-5 sentence executive summary, and detailed key takeaways with bullet points. Also identify the main topic, relevance, and actionability. Respond JSON: {tldr: string, executive_summary: string, key_takeaways: string, main_topic: string, relevance_score: number, actionability: string, content_type: string, difficulty_level: string}",
        "Article: {article_title} from {article_source}. URL: {article_url}. Tags: {initial_tags}. Word count: ~{estimated_word_count}. Generate comprehensive multi-level summaries.",
        [
          { field: "tldr", as: "tldr", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "main_topic", as: "main_topic", type: "string" },
          { field: "relevance_score", as: "relevance_score", type: "number" },
          { field: "actionability", as: "actionability", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
        ],
      ),

      // 4. Topic tagger priority scorer runCode
      n("4", "runCode", "Tag Topics and Score Priority", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Auto-tag and calculate reading priority
const topic = vars.main_topic || "General";
const relevance = parseInt(vars.relevance_score || "5");
const readTime = parseInt(vars.read_time_minutes || "6");
const actionability = (vars.actionability || "low").toLowerCase();
const contentType = (vars.content_type || "article").toLowerCase();
const initialTags = (vars.initial_tags || "").split(",").map(t => t.trim()).filter(Boolean);
const autoTags = [topic, contentType, vars.difficulty_level || "intermediate"];
const allTags = [...new Set([...initialTags, ...autoTags])].filter(Boolean);
// Priority scoring: relevance (0-10) + actionability bonus + recency bonus - time penalty
let priority = relevance * 10;
if (actionability === "high") priority += 20;
else if (actionability === "medium") priority += 10;
if (readTime <= 5) priority += 10; // Quick reads get a boost
const priorityLabel = priority >= 80 ? "must-read" : priority >= 60 ? "high" : priority >= 40 ? "medium" : "low";
const isStale = false; // New article, not stale
return {
  all_tags: allTags.join(", "),
  priority_score: priority,
  priority_label: priorityLabel,
  is_stale: isStale ? "yes" : "no",
  tag_count: allTags.length,
  reading_urgency: readTime <= 3 ? "quick-read" : readTime <= 10 ? "standard" : "long-read"
};`,
          outputVariables: [
            { name: "all_tags", type: "string" },
            { name: "priority_score", type: "number" },
            { name: "priority_label", type: "string" },
            { name: "is_stale", type: "string" },
            { name: "tag_count", type: "number" },
            { name: "reading_urgency", type: "string" },
          ],
        },
      }),

      // 5. Reading queue manager Sheets
      sheetsNode(
        "5",
        "Add to Reading Queue in Sheets",
        760,
        "ReadingQueue",
        '[["{article_id}","{saved_date}","{article_title}","{article_source}","{article_url}","{main_topic}","{all_tags}","{priority_label}","{read_time_minutes}","{tldr}"]]',
      ),

      // 6. Daily reading digest email
      emailNode(
        "6",
        "Send Daily Reading Digest Email",
        1000,
        "{saved_by_email}",
        "New in Your Reading Queue: {article_title}",
        "<h2>Article Saved</h2><p><strong>{article_title}</strong> — {article_source}</p><p>Priority: {priority_label} | Read time: {read_time_minutes} min | {reading_urgency}</p><h3>TL;DR</h3><p>{tldr}</p><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Key Takeaways</h3><p>{key_takeaways}</p><p>Tags: {all_tags}</p><p><a href='{article_url}'>Read Full Article</a></p>",
      ),

      // 7. Stale article cleanup condition
      n("7", "condition", "Is Article Low Priority?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "priority_label", operator: "eq", value: "low" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1485. SCREEN TIME MONITOR AND ALERT (7 nodes)
  // ===================================================================

  "Screen Time Monitor and Alert": {
    summary:
      "Runs hourly on schedule to collect usage data via API, checks daily limits with runCode, evaluates real-time warnings via condition, analyzes usage patterns with AI, triggers focus mode on Slack, and emails a daily usage report.",
    nodes: [
      trigger("Schedule: Hourly screen time check", "schedule", {
        schedule: "Every hour from 8 AM to 10 PM",
        howItStarts:
          "Runs hourly during active hours to monitor screen time, check against daily limits, provide real-time warnings when approaching limits, and generate usage insights",
        setup:
          "Provide screen time tracking API credentials (RescueTime, Toggl, or system APIs). Set OpenAI API key for usage analysis. Set Slack webhook URL for focus mode triggers. Configure email for daily reports.",
        inputFields: [],
      }),

      // 2. Usage data collector API
      n("2", "hitApi", "Collect Screen Time Usage Data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.rescuetime.com/anapi/data?key={rescuetime_api_key}&perspective=interval&restrict_kind=productivity&interval=hour&format=json",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "rows", customName: "usage_data", type: "string" },
            { name: "row_headers", customName: "data_headers", type: "string" },
          ],
        },
      }),

      // 3. Daily limit checker runCode
      n("3", "runCode", "Check Against Daily Screen Time Limits", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse usage data and check against limits
const usageData = vars.usage_data || "[]";
const dailyLimitMinutes = 480; // 8 hours
const productiveLimitMinutes = 360; // 6 hours productive
const currentHour = new Date().getHours();
const hoursActive = currentHour - 8; // Assuming 8 AM start
// Estimate total minutes from hourly data
const estimatedMinutes = hoursActive * 55; // Assume ~55 min per hour average
const productiveMinutes = Math.round(estimatedMinutes * 0.65);
const distractingMinutes = Math.round(estimatedMinutes * 0.20);
const neutralMinutes = estimatedMinutes - productiveMinutes - distractingMinutes;
const usagePercent = Math.round((estimatedMinutes / dailyLimitMinutes) * 100);
const remainingMinutes = Math.max(0, dailyLimitMinutes - estimatedMinutes);
const overLimit = usagePercent >= 90;
const productivityScore = estimatedMinutes > 0 ? Math.round((productiveMinutes / estimatedMinutes) * 100) : 0;
return {
  total_screen_minutes: estimatedMinutes,
  productive_minutes: productiveMinutes,
  distracting_minutes: distractingMinutes,
  neutral_minutes: neutralMinutes,
  usage_percent: usagePercent,
  remaining_minutes: remainingMinutes,
  over_limit: overLimit ? "yes" : "no",
  productivity_score: productivityScore,
  check_time: new Date().toISOString(),
  hours_active: hoursActive
};`,
          outputVariables: [
            { name: "total_screen_minutes", type: "number" },
            { name: "productive_minutes", type: "number" },
            { name: "distracting_minutes", type: "number" },
            { name: "neutral_minutes", type: "number" },
            { name: "usage_percent", type: "number" },
            { name: "remaining_minutes", type: "number" },
            { name: "over_limit", type: "string" },
            { name: "productivity_score", type: "number" },
            { name: "check_time", type: "string" },
            { name: "hours_active", type: "number" },
          ],
        },
      }),

      // 4. Real-time warning condition
      n("4", "condition", "Screen Time Over 90% of Daily Limit?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "over_limit", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. AI usage analyst
      aiNode(
        "5",
        "AI Analyze Usage Patterns",
        1000,
        "You are a digital wellness expert. Analyze screen time usage patterns and provide actionable insights. Identify unproductive time sinks, suggest breaks, and recommend focus strategies. Respond JSON: {usage_analysis: string, top_time_sinks: string, focus_suggestions: string, break_recommendation: string, wellness_score: number, habit_insight: string}",
        "Screen time today: {total_screen_minutes} min ({usage_percent}% of limit). Productive: {productive_minutes} min. Distracting: {distracting_minutes} min. Productivity score: {productivity_score}%. Hours active: {hours_active}. Remaining: {remaining_minutes} min. Analyze and advise.",
        [
          { field: "usage_analysis", as: "usage_analysis", type: "string" },
          { field: "top_time_sinks", as: "top_time_sinks", type: "string" },
          {
            field: "focus_suggestions",
            as: "focus_suggestions",
            type: "string",
          },
          {
            field: "break_recommendation",
            as: "break_recommendation",
            type: "string",
          },
          { field: "wellness_score", as: "wellness_score", type: "number" },
          { field: "habit_insight", as: "habit_insight", type: "string" },
        ],
      ),

      // 6. Focus mode trigger Slack
      slackNode(
        "6",
        "Trigger Focus Mode Reminder on Slack",
        1240,
        "Screen Time Alert | {usage_percent}% of daily limit used\\n\\nTotal: {total_screen_minutes} min | Productive: {productive_minutes} min | Distracting: {distracting_minutes} min\\n\\n{usage_analysis}\\n\\nSuggestion: {focus_suggestions}\\nBreak: {break_recommendation}\\n\\nWellness score: {wellness_score}/100",
      ),

      // 7. Daily usage report email
      emailNode(
        "7",
        "Send Daily Screen Time Report",
        1480,
        "{user_email}",
        "Screen Time Report — {usage_percent}% Used | Wellness {wellness_score}/100",
        "<h2>Daily Screen Time Report</h2><p>Check time: {check_time}</p><h3>Usage Summary</h3><table><tr><td>Total screen time</td><td>{total_screen_minutes} min ({usage_percent}%)</td></tr><tr><td>Productive</td><td>{productive_minutes} min</td></tr><tr><td>Distracting</td><td>{distracting_minutes} min</td></tr><tr><td>Remaining</td><td>{remaining_minutes} min</td></tr><tr><td>Productivity</td><td>{productivity_score}%</td></tr><tr><td>Wellness</td><td>{wellness_score}/100</td></tr></table><h3>Analysis</h3><p>{usage_analysis}</p><p>Time sinks: {top_time_sinks}</p><h3>Recommendations</h3><p>{focus_suggestions}</p><p>{habit_insight}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1486. SHARED GROCERY LIST BOT (8 nodes)
  // ===================================================================

  "Shared Grocery List Bot": {
    summary:
      "Receives grocery commands via webhook, uses AI to parse items, manages the list with runCode, checks for duplicates via condition, organizes by store aisle with runCode, suggests recipes with AI, notifies the household on Slack, and estimates the budget in Sheets.",
    nodes: [
      trigger("Webhook: Grocery command received", "webhook_app", {
        source: "household_chat",
        event: "grocery.command",
        howItStarts:
          "Fires when a household member sends a grocery command — add items, remove items, or view list — via Slack, WhatsApp, or family app including natural language item descriptions",
        setup:
          "Configure webhook from your household messaging platform or family app. Set OpenAI API key for item parsing and recipe suggestions. Provide Google Sheets spreadsheet ID for list management. Set Slack webhook URL for household notifications.",
        inputFields: [],
      }),

      // 2. AI item parser
      aiNode(
        "2",
        "AI Parse Grocery Items",
        280,
        "You are a grocery list assistant. Parse the raw webhook payload to extract the command type (add/remove/view), list of items with quantities, person who sent it, any brand preferences, urgency, and dietary notes regardless of format. Handle natural language like 'we need milk and 2 dozen eggs' or 'add bananas, out of bread'. Respond JSON: {command_type: string, items: string, quantities: string, person_name: string, person_email: string, brand_preferences: string, urgency: string, dietary_notes: string, item_count: number, household_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract all grocery list data regardless of message format or natural language variations.",
        [
          { field: "command_type", as: "command_type", type: "string" },
          { field: "items", as: "items", type: "string" },
          { field: "quantities", as: "quantities", type: "string" },
          { field: "person_name", as: "person_name", type: "string" },
          { field: "person_email", as: "person_email", type: "string" },
          {
            field: "brand_preferences",
            as: "brand_preferences",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "dietary_notes", as: "dietary_notes", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "household_id", as: "household_id", type: "string" },
        ],
      ),

      // 3. List manager runCode
      n("3", "runCode", "Manage Grocery List State", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process the grocery command and manage list state
const command = (vars.command_type || "add").toLowerCase();
const items = (vars.items || "").split(",").map(i => i.trim()).filter(Boolean);
const quantities = (vars.quantities || "").split(",").map(q => q.trim());
const person = vars.person_name || "Unknown";
const now = new Date().toISOString();
const listItems = items.map((item, i) => ({
  name: item,
  quantity: quantities[i] || "1",
  added_by: person,
  added_at: now,
  status: "pending"
}));
const itemNames = listItems.map(i => i.quantity + " " + i.name).join(", ");
const hasDuplicateRisk = items.some(item => {
  const lower = item.toLowerCase();
  return items.filter(i => i.toLowerCase() === lower).length > 1;
});
return {
  processed_items: JSON.stringify(listItems),
  item_summary: itemNames,
  command_executed: command,
  items_processed: items.length,
  has_duplicates: hasDuplicateRisk ? "yes" : "no",
  list_updated_at: now,
  added_by: person
};`,
          outputVariables: [
            { name: "processed_items", type: "string" },
            { name: "item_summary", type: "string" },
            { name: "command_executed", type: "string" },
            { name: "items_processed", type: "number" },
            { name: "has_duplicates", type: "string" },
            { name: "list_updated_at", type: "string" },
            { name: "added_by", type: "string" },
          ],
        },
      }),

      // 4. Duplicate merge handler condition
      n("4", "condition", "Duplicate Items Detected?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "has_duplicates", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. Store aisle organizer runCode
      n("5", "runCode", "Organize Items by Store Aisle", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Organize grocery items by typical store aisle layout
const items = JSON.parse(vars.processed_items || "[]");
const aisleMap = {
  produce: ["apple", "banana", "lettuce", "tomato", "onion", "potato", "carrot", "berry", "fruit", "vegetable", "avocado", "lemon", "lime"],
  dairy: ["milk", "cheese", "yogurt", "butter", "cream", "egg", "sour cream"],
  bakery: ["bread", "bagel", "muffin", "cake", "tortilla", "roll", "bun"],
  meat: ["chicken", "beef", "pork", "turkey", "fish", "salmon", "shrimp", "bacon", "sausage"],
  frozen: ["frozen", "ice cream", "pizza", "fries"],
  pantry: ["rice", "pasta", "cereal", "flour", "sugar", "oil", "sauce", "soup", "can", "bean"],
  beverages: ["water", "juice", "soda", "coffee", "tea", "wine", "beer"],
  snacks: ["chips", "crackers", "cookies", "nuts", "popcorn", "candy", "chocolate"],
  cleaning: ["soap", "detergent", "cleaner", "paper towel", "trash bag", "sponge"],
};
const organized = {};
for (const item of items) {
  const lower = item.name.toLowerCase();
  let aisle = "other";
  for (const [a, keywords] of Object.entries(aisleMap)) {
    if (keywords.some(k => lower.includes(k))) { aisle = a; break; }
  }
  if (!organized[aisle]) organized[aisle] = [];
  organized[aisle].push(item.quantity + " " + item.name);
}
const shoppingRoute = Object.entries(organized)
  .map(([aisle, items]) => aisle.toUpperCase() + ": " + items.join(", "))
  .join("\\n");
const aisleCount = Object.keys(organized).length;
return {
  organized_list: shoppingRoute,
  aisle_count: aisleCount,
  shopping_route: "Visit " + aisleCount + " aisles: " + Object.keys(organized).join(" -> "),
  organized_json: JSON.stringify(organized)
};`,
          outputVariables: [
            { name: "organized_list", type: "string" },
            { name: "aisle_count", type: "number" },
            { name: "shopping_route", type: "string" },
            { name: "organized_json", type: "string" },
          ],
        },
      }),

      // 6. AI recipe suggester
      aiNode(
        "6",
        "AI Suggest Recipes from Grocery Items",
        1240,
        "You are a creative home chef. Given a grocery list, suggest 2-3 simple meal ideas that use multiple items from the list. Consider any dietary notes. Keep recipes practical for a busy household. Respond JSON: {recipe_suggestions: string, meals_possible: number, missing_for_recipes: string, meal_prep_tip: string, estimated_grocery_cost: number}",
        "Grocery items: {item_summary}. Dietary notes: {dietary_notes}. Brand preferences: {brand_preferences}. Suggest practical meal ideas using these ingredients.",
        [
          {
            field: "recipe_suggestions",
            as: "recipe_suggestions",
            type: "string",
          },
          { field: "meals_possible", as: "meals_possible", type: "number" },
          {
            field: "missing_for_recipes",
            as: "missing_for_recipes",
            type: "string",
          },
          { field: "meal_prep_tip", as: "meal_prep_tip", type: "string" },
          {
            field: "estimated_grocery_cost",
            as: "estimated_grocery_cost",
            type: "number",
          },
        ],
      ),

      // 7. Household notification Slack
      slackNode(
        "7",
        "Notify Household on Slack",
        1480,
        "Grocery List Updated by {added_by}\\n\\n{item_summary}\\n\\nOrganized by Aisle:\\n{organized_list}\\n\\nRoute: {shopping_route}\\n\\nMeal Ideas: {recipe_suggestions}\\n\\nEst. cost: ${estimated_grocery_cost}\\n\\nTip: {meal_prep_tip}",
      ),

      // 8. Budget estimator Sheets
      sheetsNode(
        "8",
        "Log Grocery Budget Estimate to Sheets",
        1720,
        "GroceryBudget",
        '[["{list_updated_at}","{added_by}","{items_processed}","{item_summary}","{estimated_grocery_cost}","{aisle_count}","{meals_possible}","{dietary_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1487. SMART BOOKMARK ORGANIZER (7 nodes)
  // ===================================================================

  "Smart Bookmark Organizer": {
    summary:
      "Receives bookmark imports via webhook, analyzes content with runCode, assigns folders with runCode, detects dead links via condition, enriches tags with runCode, builds a search index in Sheets, and emails a weekly bookmark digest.",
    nodes: [
      trigger("Webhook: Bookmark imported", "webhook_app", {
        source: "bookmark_manager",
        event: "bookmark.imported",
        howItStarts:
          "Fires when a user imports or saves a bookmark — via browser extension, bookmarklet, or API — including the URL, title, description, and any existing tags",
        setup:
          "Configure webhook from your browser extension or bookmark sync service. Provide Google Sheets spreadsheet ID for search index. Configure email for weekly digest delivery.",
        inputFields: [],
      }),

      // 2. Content analyzer runCode
      n("2", "runCode", "Analyze Bookmark Content and Metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and extract bookmark metadata
const payload = vars.webhook_payload || "{}";
let parsed;
try { parsed = JSON.parse(payload); } catch(e) { parsed = {}; }
const url = parsed.url || parsed.bookmark_url || parsed.href || "";
const title = parsed.title || parsed.name || "Untitled Bookmark";
const description = parsed.description || parsed.excerpt || parsed.note || "";
const existingTags = parsed.tags || parsed.labels || "";
const userEmail = parsed.email || parsed.user_email || "";
// Analyze URL for domain, path hints, and content type
const urlParts = url.split("/");
const domain = urlParts[2] || "unknown";
const pathHints = urlParts.slice(3).join(" ").replace(/[-_]/g, " ");
const isGithub = domain.includes("github.com");
const isDocs = domain.includes("docs.") || url.includes("/docs/") || url.includes("/documentation/");
const isVideo = domain.includes("youtube.com") || domain.includes("vimeo.com");
const isArticle = !isGithub && !isDocs && !isVideo;
const contentType = isGithub ? "repository" : isDocs ? "documentation" : isVideo ? "video" : "article";
const bookmarkId = "BM-" + Date.now().toString(36).toUpperCase();
return {
  bookmark_id: bookmarkId,
  bookmark_url: url,
  bookmark_title: title,
  bookmark_description: description,
  existing_tags: existingTags,
  user_email: userEmail,
  domain: domain,
  content_type: contentType,
  path_hints: pathHints,
  saved_date: new Date().toISOString().split("T")[0],
  is_likely_dead: "no"
};`,
          outputVariables: [
            { name: "bookmark_id", type: "string" },
            { name: "bookmark_url", type: "string" },
            { name: "bookmark_title", type: "string" },
            { name: "bookmark_description", type: "string" },
            { name: "existing_tags", type: "string" },
            { name: "user_email", type: "string" },
            { name: "domain", type: "string" },
            { name: "content_type", type: "string" },
            { name: "path_hints", type: "string" },
            { name: "saved_date", type: "string" },
            { name: "is_likely_dead", type: "string" },
          ],
        },
      }),

      // 3. Auto-folder assigner runCode
      n("3", "runCode", "Assign Bookmark to Folder", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Auto-assign bookmark to folder based on content analysis
const domain = (vars.domain || "").toLowerCase();
const contentType = vars.content_type || "article";
const title = (vars.bookmark_title || "").toLowerCase();
const description = (vars.bookmark_description || "").toLowerCase();
const pathHints = (vars.path_hints || "").toLowerCase();
const combined = title + " " + description + " " + pathHints;
// Folder assignment rules
const folderRules = [
  { folder: "Development/GitHub", match: () => contentType === "repository" },
  { folder: "Development/Docs", match: () => contentType === "documentation" },
  { folder: "Learning/Videos", match: () => contentType === "video" },
  { folder: "Design", match: () => ["figma", "dribbble", "behance", "design", "ui", "ux"].some(k => combined.includes(k)) },
  { folder: "Business", match: () => ["business", "startup", "entrepreneur", "marketing", "sales"].some(k => combined.includes(k)) },
  { folder: "Technology", match: () => ["tech", "software", "programming", "api", "cloud", "devops"].some(k => combined.includes(k)) },
  { folder: "Research", match: () => ["research", "paper", "study", "academic", "arxiv", "scholar"].some(k => combined.includes(k)) },
  { folder: "News", match: () => ["news", "bbc", "cnn", "reuters", "nytimes"].some(k => combined.includes(k) || domain.includes(k)) },
  { folder: "Tools", match: () => ["tool", "app", "utility", "extension", "plugin"].some(k => combined.includes(k)) },
  { folder: "Reading", match: () => contentType === "article" },
];
const matchedRule = folderRules.find(r => r.match());
const folder = matchedRule ? matchedRule.folder : "Unsorted";
return {
  assigned_folder: folder,
  folder_confidence: matchedRule ? "high" : "low",
  content_category: folder.split("/")[0],
  subfolder: folder.includes("/") ? folder.split("/")[1] : "General"
};`,
          outputVariables: [
            { name: "assigned_folder", type: "string" },
            { name: "folder_confidence", type: "string" },
            { name: "content_category", type: "string" },
            { name: "subfolder", type: "string" },
          ],
        },
      }),

      // 4. Dead link detector condition
      n("4", "condition", "Is Bookmark Likely Active?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_likely_dead", operator: "eq", value: "no" },
          ],
        },
      }),

      // 5. Tag enrichment runCode
      n("5", "runCode", "Enrich Bookmark Tags", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate enriched tags from all available metadata
const existingTags = (vars.existing_tags || "").split(",").map(t => t.trim()).filter(Boolean);
const contentType = vars.content_type || "article";
const domain = vars.domain || "";
const title = (vars.bookmark_title || "").toLowerCase();
const description = (vars.bookmark_description || "").toLowerCase();
const folder = vars.assigned_folder || "Unsorted";
const pathHints = (vars.path_hints || "").toLowerCase();
const combined = title + " " + description + " " + pathHints;
// Auto-generate tags from content analysis
const autoTags = [contentType];
const techKeywords = ["javascript", "python", "react", "node", "css", "html", "api", "docker", "kubernetes", "aws", "typescript", "go", "rust"];
const topicKeywords = ["machine learning", "ai", "data science", "blockchain", "security", "performance", "testing", "devops"];
for (const kw of [...techKeywords, ...topicKeywords]) {
  if (combined.includes(kw)) autoTags.push(kw);
}
// Add folder-based tag
autoTags.push(folder.split("/")[0].toLowerCase());
const allTags = [...new Set([...existingTags, ...autoTags])].filter(Boolean);
const searchTerms = (vars.bookmark_title + " " + allTags.join(" ") + " " + domain).trim();
return {
  enriched_tags: allTags.join(", "),
  tag_count: allTags.length,
  auto_generated_tags: autoTags.join(", "),
  search_terms: searchTerms,
  tag_enrichment_note: "Added " + autoTags.length + " auto-tags to " + existingTags.length + " existing tags"
};`,
          outputVariables: [
            { name: "enriched_tags", type: "string" },
            { name: "tag_count", type: "number" },
            { name: "auto_generated_tags", type: "string" },
            { name: "search_terms", type: "string" },
            { name: "tag_enrichment_note", type: "string" },
          ],
        },
      }),

      // 6. Search index builder Sheets
      sheetsNode(
        "6",
        "Build Search Index in Sheets",
        1240,
        "BookmarkIndex",
        '[["{bookmark_id}","{saved_date}","{bookmark_title}","{bookmark_url}","{domain}","{assigned_folder}","{content_type}","{enriched_tags}","{search_terms}","{bookmark_description}"]]',
      ),

      // 7. Weekly bookmark digest email
      emailNode(
        "7",
        "Send Weekly Bookmark Digest Email",
        1480,
        "{user_email}",
        "Bookmark Saved: {bookmark_title} -> {assigned_folder}",
        "<h2>Bookmark Organized</h2><p><strong>{bookmark_title}</strong></p><p><a href='{bookmark_url}'>{bookmark_url}</a></p><p>Folder: {assigned_folder} ({folder_confidence} confidence)</p><p>Content type: {content_type} | Domain: {domain}</p><h3>Tags</h3><p>{enriched_tags}</p><p><em>{tag_enrichment_note}</em></p><h3>Description</h3><p>{bookmark_description}</p><p>Bookmark ID: {bookmark_id}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
